import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { auth, firestore } from '../firebase';
import { signOut } from 'firebase/auth';
import { collection, onSnapshot, orderBy, query, addDoc, deleteDoc, doc, updateDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import ProspectionSection     from './ProspectionSection';
import ProspectionFreeSection from './ProspectionFreeSection';
import PreferencesSection  from './PreferencesSection';
import AnalyticsSection    from './AnalyticsSection';
import SEOSection          from './SEOSection';
import AccessSection          from './AccessSection';
import LegalGeneratorSection  from './LegalGeneratorSection';
import MarketingSection       from './MarketingSection';
import TasksSection           from './TasksSection';
import GmbSection             from './GmbSection';
import ItemsSection           from './ItemsSection';
import QuoteCalculatorSection from './QuoteCalculatorSection';
import DomainCheckerSection   from './DomainCheckerSection';
import QRGeneratorSection     from './QRGeneratorSection';
import BrandPaletteSection    from './BrandPaletteSection';
import ThemeSection           from './ThemeSection';
import VibeeAdminSection      from './VibeeAdminSection';
import VibeeRevenueSection    from './VibeeRevenueSection';
import VibeeStatsSection      from './VibeeStatsSection';
import GraphViewSection       from './GraphViewSection';
import QSUsersSection         from './QSUsersSection';
import ErrorBoundary          from '../shared/ErrorBoundary';
import InvoiceModal, { printInvoice } from './InvoiceModal';
import { useTheme }           from '../context/ThemeContext';

const API = process.env.REACT_APP_API_URL || '';
const ax = axios.create({ baseURL: API, withCredentials: true });

ax.interceptors.request.use(cfg => {
  if (!['get','head','options'].includes(cfg.method)) {
    const token = document.cookie.split('; ').find(r => r.startsWith('XSRF-TOKEN='))?.split('=')[1];
    if (token) cfg.headers['x-csrf-token'] = decodeURIComponent(token);
  }
  return cfg;
});

// ─── ICONS ────────────────────────────────────────────────────────────────────
const I = {
  cmd:     <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>,
  clients: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="9" cy="7" r="4"/><path d="M2 21v-2a7 7 0 0 1 14 0v2"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  globe:   <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  folder:  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
  msg:     <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  out:     <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  plus:    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  pen:     <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  bin:     <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  link:    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  search:  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  arrow:   <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  contract:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  spark:   <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z"/></svg>,
  print:   <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>,
};

// ─── STATUS MAPS ──────────────────────────────────────────────────────────────
const CLIENT_STAGES = {
  prospect:    { label: 'Prospect',      color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  devis:       { label: 'Devis envoyé',  color: '#fbbf24', bg: 'rgba(251,191,36,0.1)' },
  active:      { label: 'En production', color: '#C8FF00', bg: 'rgba(200,255,0,0.1)'  },
  completed:   { label: 'Livré',         color: '#4ade80', bg: 'rgba(74,222,128,0.1)' },
  maintenance: { label: 'Maintenance',   color: '#a78bfa', bg: 'rgba(167,139,250,0.1)'},
  inactive:    { label: 'Inactif',       color: '#444',    bg: 'rgba(255,255,255,0.04)'},
};

const WEB_STATUS = {
  en_dev:      { label: 'En développement', color: '#C8FF00', glow: 'rgba(200,255,0,0.25)',    border: 'rgba(200,255,0,0.3)'    },
  en_test:     { label: 'En test',          color: '#60a5fa', glow: 'rgba(96,165,250,0.25)',   border: 'rgba(96,165,250,0.3)'   },
  en_ligne:    { label: 'En ligne',         color: '#4ade80', glow: 'rgba(74,222,128,0.25)',   border: 'rgba(74,222,128,0.3)'   },
  maintenance: { label: 'Maintenance',      color: '#a78bfa', glow: 'rgba(167,139,250,0.25)',  border: 'rgba(167,139,250,0.3)'  },
  pause:       { label: 'En pause',         color: '#555',    glow: 'transparent',             border: 'rgba(255,255,255,0.08)' },
};

const EMPTY_CLIENT  = { name:'', email:'', phone:'', company:'', project:'', budget:'', status:'prospect', notes:'' };
const EMPTY_WEBSITE = { name:'', client:'', url:'', status:'en_dev', techStack:'', progress:0, budget:'', dueDate:'', notes:'', logoUrl:'' };
const EMPTY_PROJECT = { name:'', client:'', progress:0, status:'active', dueDate:'' };
const EMPTY_INVOICE = { clientName:'', clientEmail:'', clientPhone:'', clientCompany:'', lines:[{desc:'', qty:1, unitPrice:''}], notes:'', status:'en_attente', dueDate:'', taxable:true };

const INVOICE_STATUS = {
  payé:       { label:'Payé',       color:'#4ade80', bg:'rgba(74,222,128,0.1)'  },
  en_attente: { label:'En attente', color:'#fbbf24', bg:'rgba(251,191,36,0.1)'  },
  en_retard:  { label:'En retard',  color:'#f87171', bg:'rgba(248,113,113,0.1)' },
};

const dollar = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;

// ─── TINY COMPONENTS ──────────────────────────────────────────────────────────
const Dot = ({ color, glow }) => (
  <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background:color, boxShadow:`0 0 8px ${glow||color}`, flexShrink:0 }}/>
);

const Badge = ({ status, map = CLIENT_STAGES }) => {
  const s = map[status] || { label: status, color:'#555', bg:'rgba(255,255,255,0.05)' };
  return <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 9px', borderRadius:100, fontSize:'0.68rem', fontWeight:700, color:s.color, background:s.bg, letterSpacing:'0.04em', whiteSpace:'nowrap', textTransform:'uppercase' }}>{s.label}</span>;
};

const Avatar = ({ name='?', size=34 }) => {
  const initials = name.split(' ').slice(0,2).map(w=>w[0]||'').join('').toUpperCase() || '?';
  const hue = [...name].reduce((a,c)=>a+c.charCodeAt(0),0) % 360;
  return <div style={{ width:size, height:size, borderRadius:'50%', background:`hsl(${hue},45%,22%)`, border:`1.5px solid hsl(${hue},45%,38%)`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:`hsl(${hue},60%,75%)`, fontSize:size*0.36, flexShrink:0, letterSpacing:'-0.02em' }}>{initials}</div>;
};

const Bar = ({ value=0, color='#C8FF00', height=5 }) => (
  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
    <div style={{ flex:1, height, background:'rgba(255,255,255,0.06)', borderRadius:99, overflow:'hidden' }}>
      <div style={{ height:'100%', width:`${Math.min(value,100)}%`, background:color, borderRadius:99, transition:'width 0.8s cubic-bezier(.4,0,.2,1)' }}/>
    </div>
    <span style={{ fontSize:'0.72rem', color:'#444', minWidth:28, textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{value}%</span>
  </div>
);

const Field = ({ label, value, half }) => (
  <div style={{ marginBottom:14, ...(half ? {} : {}) }}>
    <label style={{ display:'block', fontSize:'0.7rem', color:'#444', marginBottom:5, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>{label}</label>
    <input readOnly={false} style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'10px 12px', color:'#EAEAEA', fontSize:'0.88rem', outline:'none', boxSizing:'border-box', transition:'border-color 0.15s' }}
      onFocus={e=>e.target.style.borderColor='var(--accent-border,rgba(200,255,0,0.4))'}
      onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.08)'}
      value={value.value} onChange={value.onChange} type={value.type||'text'} placeholder={value.placeholder||''}/>
  </div>
);

const Sel = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom:14 }}>
    <label style={{ display:'block', fontSize:'0.7rem', color:'#444', marginBottom:5, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>{label}</label>
    <select style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'10px 12px', color:'#EAEAEA', fontSize:'0.88rem', outline:'none', appearance:'none', cursor:'pointer' }} value={value} onChange={e=>onChange(e.target.value)}>
      {options.map(o=><option key={o.v} value={o.v} style={{background:'#111'}}>{o.l}</option>)}
    </select>
  </div>
);

const Modal = ({ title, onClose, onSave, saving, children }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', backdropFilter:'blur(8px)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }} onClick={onClose}>
    <div style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.08)', borderRadius:16, padding:32, width:'100%', maxWidth:540, maxHeight:'88vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
        <h3 style={{ fontSize:'1.15rem', fontWeight:800, color:'#EAEAEA', letterSpacing:'-0.02em' }}>{title}</h3>
        <button style={{ background:'none', border:'none', color:'#444', cursor:'pointer', fontSize:'1.5rem', lineHeight:1, padding:4 }} onClick={onClose}>×</button>
      </div>
      {children}
      <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:24, paddingTop:20, borderTop:'1px solid rgba(255,255,255,0.05)' }}>
        <button style={btn.ghost} onClick={onClose}>Annuler</button>
        <button style={{ ...btn.primary, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }} onClick={onSave} disabled={saving}>
          {saving ? <><span style={{ display:'inline-block', width:12, height:12, border:'2px solid #000', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/> En cours...</> : 'Enregistrer'}
        </button>
      </div>
    </div>
  </div>
);

const Confirm = ({ name, onConfirm, onClose }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', backdropFilter:'blur(8px)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
    <div style={{ background:'#0e0e12', border:'1px solid rgba(248,113,113,0.2)', borderRadius:14, padding:28, maxWidth:360, width:'90%', textAlign:'center' }} onClick={e=>e.stopPropagation()}>
      <div style={{ fontSize:'1.8rem', marginBottom:10 }}>⚠</div>
      <div style={{ color:'#EAEAEA', fontWeight:700, marginBottom:6 }}>Supprimer "{name}" ?</div>
      <div style={{ color:'#444', fontSize:'0.82rem', marginBottom:22 }}>Action irréversible.</div>
      <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
        <button style={btn.ghost} onClick={onClose}>Annuler</button>
        <button style={{ ...btn.primary, background:'#f87171', color:'#fff' }} onClick={onConfirm}>Supprimer</button>
      </div>
    </div>
  </div>
);

function EditDates({ u, onSave }) {
  const [start, setStart] = React.useState(u.startDate || '');
  const [end,   setEnd]   = React.useState(u.endDate   || '');
  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:8, padding:'0 14px 12px', alignItems:'flex-end' }}>
      {[['Début', start, setStart], ['Fin', end, setEnd]].map(([lbl, val, set]) => (
        <div key={lbl}>
          <div style={{ fontSize:'0.6rem', color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:3 }}>{lbl}</div>
          <input type="date" value={val} onChange={e=>set(e.target.value)}
            style={{ width:'100%', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:7, padding:'7px 9px', color:'#EAEAEA', fontSize:'0.8rem', outline:'none', fontFamily:'inherit', boxSizing:'border-box', colorScheme:'dark' }}/>
        </div>
      ))}
      <button onClick={()=>onSave(u.uid, start, end)}
        style={{ background:'#a78bfa', border:'none', borderRadius:7, padding:'7px 12px', color:'#000', fontWeight:700, fontSize:'0.78rem', cursor:'pointer', whiteSpace:'nowrap' }}>
        ✓ OK
      </button>
    </div>
  );
}

const IconBtn = ({ icon, onClick, danger }) => (
  <button onClick={onClick} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28, borderRadius:6, border:`1px solid ${danger?'rgba(248,113,113,0.2)':'rgba(255,255,255,0.07)'}`, background:'transparent', color:danger?'#f87171':'#555', cursor:'pointer', transition:'all 0.15s', flexShrink:0 }}
    onMouseEnter={e=>{e.currentTarget.style.borderColor=danger?'rgba(248,113,113,0.5)':'rgba(255,255,255,0.2)'; e.currentTarget.style.color=danger?'#fca5a5':'#EAEAEA';}}
    onMouseLeave={e=>{e.currentTarget.style.borderColor=danger?'rgba(248,113,113,0.2)':'rgba(255,255,255,0.07)'; e.currentTarget.style.color=danger?'#f87171':'#555';}}>
    {icon}
  </button>
);

const btn = {
  primary: { display:'inline-flex', alignItems:'center', gap:7, background:'var(--accent,#C8FF00)', color:'#000', border:'none', padding:'9px 20px', borderRadius:8, fontWeight:800, fontSize:'0.82rem', cursor:'pointer', letterSpacing:'0.02em' },
  ghost:   { display:'inline-flex', alignItems:'center', gap:7, background:'transparent', color:'#555', border:'1px solid rgba(255,255,255,0.08)', padding:'9px 18px', borderRadius:8, fontWeight:600, fontSize:'0.82rem', cursor:'pointer' },
  outline: { display:'inline-flex', alignItems:'center', gap:7, background:'transparent', color:'var(--accent,#C8FF00)', border:'1px solid var(--accent-border,rgba(200,255,0,0.3))', padding:'7px 14px', borderRadius:7, fontWeight:700, fontSize:'0.78rem', cursor:'pointer' },
};

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return w;
}

export default function Dashboard() {
  const { currentUser, userRole, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const { accent, setAccent } = useTheme();
  const [sec, setSec] = useState(0);
  const [checklistHidden, setChecklistHidden] = useState(() => !!localStorage.getItem('checklist-dismissed'));
  const isMobile = useWindowWidth() < 768;

  const [clients,  setClients]  = useState([]);
  const [websites, setWebsites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [audits,            setAudits]            = useState([]);
  const [competitorReports, setCompetitorReports] = useState([]);
  const [gmbReports,        setGmbReports]        = useState([]);
  const [vibeeReportCount,  setVibeeReportCount]  = useState(0);
  const [busy, setBusy] = useState(true);

  // Search + filter state
  const [cSearch, setCSearch]   = useState('');
  const [cStage,  setCStage]    = useState('all');
  const [wStatus, setWStatus]   = useState('all');
  const [wSearch, setWSearch]   = useState('');

  // Modals
  const [cModal,   setCModal]   = useState(null);
  const [wModal,   setWModal]   = useState(null);
  const [pModal,   setPModal]   = useState(null);
  const [iModal,   setIModal]   = useState(null);
  const [delTarget,setDelTarget]= useState(null);
  const [selClient,setSelClient]= useState(null);
  const [selMsg,   setSelMsg]   = useState(null);
  const [newSiteReady, setNewSiteReady] = useState(null);
  const [aiLoading,    setAiLoading]    = useState(false);
  const [aiPreview,    setAiPreview]    = useState(null);
  const [aiFactory,       setAiFactory]       = useState(null);
  const [aiFactoryLoading,setAiFactoryLoading]= useState(false);
  const [aiFactoryResult, setAiFactoryResult] = useState(null);
  const [aiFactoryError,  setAiFactoryError]  = useState('');
  const [siteEditor,   setSiteEditor]   = useState(null); // { target, config }
  const [siteEditorTab,setSiteEditorTab]= useState('contact');
  const [siteEditorSaving,setSiteEditorSaving] = useState(false);
  const [ftpModal,     setFtpModal]     = useState(null);
  const [ftpForm,      setFtpForm]      = useState({ host:'', user:'', password:'', remoteDir:'/' });
  const [ftpLog,       setFtpLog]       = useState([]);
  const [ftpRunning,   setFtpRunning]   = useState(false);
  const [fbModal,      setFbModal]      = useState(null); // { target, name }
  const [fbLog,        setFbLog]        = useState([]);
  const [fbRunning,    setFbRunning]    = useState(false);
  const [fbDoneUrl,    setFbDoneUrl]    = useState(null);
  const [wSaving,      setWSaving]      = useState(false);
  const countdownRef = useRef(null);

  // ── Backups sources sites ──
  const [siteBackups,       setSiteBackups]       = useState([]);
  const [showSiteBackups,   setShowSiteBackups]   = useState(false);
  const [siteBackupLoading, setSiteBackupLoading] = useState(false);
  const [siteBackupMsg,     setSiteBackupMsg]     = useState('');

  // ── Contrats IA ──
  const [contracts,    setContracts]    = useState([]);
  const [ctModal,      setCtModal]      = useState(false);
  const [ctForm,       setCtForm]       = useState({
    clientName:'', clientCompany:'', clientEmail:'', clientPhone:'', clientAddress:'',
    projectType:'Site vitrine', projectDescription:'', budget:'', currency:'CAD',
    deposit:'50', startDate:'', endDate:'', revisions:'2',
    hosting:'Non', maintenance:'Non',
    paymentTerms:'', extras:'',
    agencyName:'Ucreative', agencyAddress:'Montréal, QC', agencyEmail:'',
  });
  const [ctGenerated,  setCtGenerated]  = useState('');
  const [ctGenerating, setCtGenerating] = useState(false);
  const [ctSaving,     setCtSaving]     = useState(false);
  const [ctDelTarget,  setCtDelTarget]  = useState(null);
  const [ctViewing,    setCtViewing]    = useState(null);

  // ── CSRF token — requis pour tous les POST ──
  useEffect(() => { ax.get('/api/csrf-token').catch(() => {}); }, []);

  // ── TEMPS RÉEL — listeners critiques seulement ──
  useEffect(() => {
    const noop = () => {};
    const unsubs = [
      onSnapshot(query(collection(firestore,'clients'),  orderBy('createdAt','desc')), s => setClients(s.docs.map(d=>({id:d.id,...d.data()}))), noop),
      onSnapshot(query(collection(firestore,'websites'), orderBy('createdAt','desc')), s => setWebsites(s.docs.map(d=>({id:d.id,...d.data()}))), noop),
      onSnapshot(query(collection(firestore,'projects'), orderBy('createdAt','desc')), s => setProjects(s.docs.map(d=>({id:d.id,...d.data()}))), noop),
      onSnapshot(query(collection(firestore,'messages'), orderBy('createdAt','desc')), s => setMessages(s.docs.map(d=>({id:d.id,...d.data()}))), noop),
      onSnapshot(query(collection(firestore,'invoices'), orderBy('createdAt','desc')), s => { setInvoices(s.docs.map(d=>({id:d.id,...d.data()}))); setBusy(false); ax.get('/api/items').then(r => setInvoiceItems(r.data || [])).catch(()=>{}); }, noop),
      onSnapshot(query(collection(firestore,'contracts'), orderBy('createdAt','desc')), s => setContracts(s.docs.map(d=>({id:d.id,...d.data()}))), noop),
      onSnapshot(collection(firestore,'vibeeReports'), s => setVibeeReportCount(s.docs.filter(d => !d.data().resolved).length), noop),
    ];
    // Lectures uniques pour données non-temps-réel
    getDocs(query(collection(firestore,'audits'),             orderBy('createdAt','desc'))).then(s => setAudits(s.docs.map(d=>({id:d.id,...d.data(), createdAt:d.data().createdAt?.toDate()?.toISOString()||null})))).catch(noop);
    getDocs(query(collection(firestore,'competitor_reports'), orderBy('createdAt','desc'))).then(s => setCompetitorReports(s.docs.map(d=>({id:d.id,...d.data(), createdAt:d.data().createdAt?.toDate()?.toISOString()||null})))).catch(noop);
    getDocs(query(collection(firestore,'gmb_audits'),         orderBy('createdAt','desc'))).then(s => setGmbReports(s.docs.map(d=>({id:d.id,...d.data(), createdAt:d.data().createdAt?.toDate()?.toISOString()||null})))).catch(noop);
    return () => unsubs.forEach(u => u());
  }, []);

  useEffect(() => { if (!loading && !currentUser) navigate('/auth'); }, [loading, currentUser, navigate]);
  useEffect(() => { return () => { if (countdownRef.current) clearInterval(countdownRef.current); }; }, []);

  if (loading || busy) return (
    <div style={{ background:'#060608', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{ width:36, height:36, border:'2px solid rgba(200,255,0,0.15)', borderTop:'2px solid var(--accent,#C8FF00)', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
      <span style={{ color:'#333', fontSize:'0.8rem', letterSpacing:'0.06em', textTransform:'uppercase' }}>Chargement</span>
    </div>
  );

  const handleLogout = async () => { await signOut(auth); navigate('/'); };

  // ── COMPUTED ──
  const stats = {
    activeClients:  clients.filter(c=>c.status==='active').length,
    sitesLive:      websites.filter(w=>w.status==='en_ligne').length,
    activeProjects: projects.filter(p=>p.status!=='completed').length,
    unread:         messages.filter(m=>!m.read).length,
    budget:         clients.reduce((a,c)=>a+(parseFloat(c.budget)||0),0),
    totalFacturé:   invoices.reduce((a,i)=>a+(i.amount||0),0),
    totalPayé:      invoices.filter(i=>i.status==='payé').reduce((a,i)=>a+(i.amount||0),0),
    enAttente:      invoices.filter(i=>i.status==='en_attente').reduce((a,i)=>a+(i.amount||0),0),
    enRetard:       invoices.filter(i=>i.status==='en_retard').length,
  };

  const stageCount = stage => clients.filter(c=>c.status===stage).length;

  // ── CRUD ──
  const saveClient = async () => {
    if (!cModal?.name) { setCModal(m => ({ ...m, _error: 'Le nom complet est requis.' })); return; }
    try {
      cModal.id ? await ax.put(`/api/clients/${cModal.id}`, cModal) : await ax.post('/api/clients', cModal);
      setCModal(null);
    } catch (e) {
      setCModal(m => ({ ...m, _error: e.response?.data?.error || e.message }));
    }
  };
  const delClient = async () => {
    await ax.delete(`/api/clients/${delTarget.id}`);
    setDelTarget(null); setSelClient(null);
  };

  const generateWithAI = async () => {
    if (!wModal?.name || !wModal?.aiDescription) return;
    setAiLoading(true);
    setAiPreview(null);
    try {
      const { data } = await ax.post('/api/ai/generate-config', {
        name:                  wModal.name,
        description:           wModal.aiDescription,
        siteType:              wModal.siteType           || 'vitrine',
        location:              wModal.location           || 'Montréal, QC',
        customPhotoUrl:        wModal.customPhotoUrl     || '',
        inspirationImageBase64:wModal.inspirationB64     || null,
        inspirationImageUrl:   wModal.inspirationUrl     || null,
      });
      setAiPreview(data.config);
    } catch (e) {
      alert('Erreur IA : ' + (e.response?.data?.error || e.message));
    }
    setAiLoading(false);
  };

  const slugify = (str) => str.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '').replace(/[^a-z0-9-]/g, '').slice(0, 24);

  const openAiFactory = () => {
    setAiFactory({ name:'', identifier:'', siteType:'vitrine', location:'Montréal, QC', description:'', customPhotoUrl:'' });
    setAiFactoryResult(null);
    setAiFactoryError('');
  };

  const createSiteWithAI = async () => {
    if (!aiFactory?.name || !aiFactory?.description) return;
    setAiFactoryLoading(true);
    setAiFactoryError('');
    try {
      const { data } = await ax.post('/api/ai/create-site', {
        name:                  aiFactory.name,
        identifier:            aiFactory.identifier || slugify(aiFactory.name),
        siteType:              aiFactory.siteType   || 'vitrine',
        location:              aiFactory.location   || 'Montréal, QC',
        description:           aiFactory.description,
        customPhotoUrl:        aiFactory.customPhotoUrl || '',
      });
      setAiFactoryResult(data);
    } catch (e) {
      setAiFactoryError(e.response?.data?.error || e.message);
    }
    setAiFactoryLoading(false);
  };

  const handleInspirationFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setWModal(m => ({ ...m, inspirationB64: e.target.result, inspirationUrl: null, inspirationName: file.name }));
    reader.readAsDataURL(file);
  };

  const openSiteEditor = async (w) => {
    if (!w.identifier) return;
    try {
      const { data } = await ax.get(`/api/dev/site-config/${w.identifier}`);
      setSiteEditor({ target: w.identifier, name: w.name, config: data });
      setSiteEditorTab('contact');
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        alert(`Fichiers introuvables pour "${w.name}".\n\nLe site existe dans Firestore mais son dossier n'est pas sur ce poste (supprimé ou créé ailleurs).\n\nSolution : recréer le site avec le même identifiant "${w.identifier}".`);
      } else {
        alert('Erreur : ' + (e.response?.data?.error || e.message));
      }
    }
  };

  const saveSiteConfig = async () => {
    if (!siteEditor) return;
    setSiteEditorSaving(true);
    try {
      await ax.put(`/api/dev/update-site/${siteEditor.target}`, siteEditor.config);
      setSiteEditor(null);
    } catch (e) {
      alert('Erreur sauvegarde : ' + (e.response?.data?.error || e.message));
    }
    setSiteEditorSaving(false);
  };

  const setEC = (patch) => setSiteEditor(ed => ({ ...ed, config: { ...ed.config, ...patch } }));
  const setEHero = (patch) => setSiteEditor(ed => ({ ...ed, config: { ...ed.config, hero: { ...ed.config.hero, ...patch } } }));
  const setEAbout = (patch) => setSiteEditor(ed => ({ ...ed, config: { ...ed.config, about: { ...ed.config.about, ...patch } } }));
  const setESocial = (patch) => setSiteEditor(ed => ({ ...ed, config: { ...ed.config, social: { ...ed.config.social, ...patch } } }));
  const setEService = (i, patch) => setSiteEditor(ed => {
    const services = [...(ed.config.services||[])];
    services[i] = { ...services[i], ...patch };
    return { ...ed, config: { ...ed.config, services } };
  });
  const setETestimonial = (i, patch) => setSiteEditor(ed => {
    const testimonials = [...(ed.config.testimonials||[])];
    testimonials[i] = { ...testimonials[i], ...patch };
    return { ...ed, config: { ...ed.config, testimonials } };
  });

  const startFtpDeploy = async () => {
    if (!ftpForm.host || !ftpForm.user || !ftpForm.password) { alert('Remplis les champs FTP'); return; }
    setFtpLog([]);
    setFtpRunning(true);
    try {
      const resp = await fetch(`${API}/api/dev/ftp-deploy/${ftpModal.target}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ftpForm),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
        setFtpLog(l => [...l, `❌ Erreur ${resp.status} : ${err.error || 'Accès refusé'}`]);
        setFtpRunning(false);
        return;
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value);
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const obj = JSON.parse(line.slice(6));
              if (obj.msg) setFtpLog(l => [...l, obj.msg]);
              if (obj.done) setFtpRunning(false);
            } catch {}
          }
        }
      }
    } catch (e) {
      setFtpLog(l => [...l, '❌ ' + e.message]);
    }
    setFtpRunning(false);
  };

  const startFirebaseDeploy = async () => {
    setFbLog([]);
    setFbRunning(true);
    setFbDoneUrl(null);
    try {
      const resp = await fetch(`${API}/api/dev/firebase-deploy/${fbModal.target}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
        setFbLog(l => [...l, `❌ Erreur ${resp.status} : ${err.error || 'Accès refusé'}`]);
        setFbRunning(false);
        return;
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value);
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const obj = JSON.parse(line.slice(6));
              if (obj.msg) setFbLog(l => [...l, obj.msg]);
              if (obj.done) { setFbRunning(false); if (obj.url) setFbDoneUrl(obj.url); }
            } catch {}
          }
        }
      }
    } catch (e) {
      setFbLog(l => [...l, '❌ ' + e.message]);
    }
    setFbRunning(false);
  };

  const saveWebsite = async () => {
    if (!wModal?.name || wSaving) return;
    setWSaving(true);
    try {
      if (wModal.id) {
        await ax.put(`/api/websites/${wModal.id}`, wModal);
        setWModal(null);
      } else {
        if (!wModal.identifier) { alert('Identifiant requis'); setWSaving(false); return; }
        await ax.post('/api/dev/create-site', {
          target:        wModal.identifier,
          name:          wModal.name,
          siteType:      wModal.siteType      || 'vitrine',
          location:      wModal.location      || 'Montréal, QC',
          aiConfig:      aiPreview            || null,
          customPhotoUrl:wModal.customPhotoUrl|| '',
          logoUrl:       wModal.logoUrl       || '',
          client:        wModal.client        || '',
          url:           wModal.url           || '',
          budget:        wModal.budget        || '',
          notes:         wModal.notes         || '',
        });
        setAiPreview(null);
        setWModal(null);
        let n = 20;
        setNewSiteReady({ target: wModal.identifier, name: wModal.name, countdown: n });
        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
          n--;
          if (n <= 0) { clearInterval(countdownRef.current); countdownRef.current = null; setNewSiteReady(r => ({ ...r, countdown: 0 })); }
          else setNewSiteReady(r => ({ ...r, countdown: n }));
        }, 1000);
      }
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      const status = e.response?.status;
      if (status === 409) {
        setWModal(m => ({ ...m, _error: `L'identifiant "${wModal.identifier}" est déjà utilisé. Choisis un autre nom.` }));
      } else {
        setWModal(m => ({ ...m, _error: msg }));
      }
    }
    setWSaving(false);
  };
  const delWebsite = async () => {
    await ax.delete(`/api/websites/${delTarget.id}`);
    setDelTarget(null);
  };

  const loadSiteBackups = async () => {
    setSiteBackupLoading(true);
    try {
      const res = await ax.get('/api/backup');
      setSiteBackups(res.data);
      setShowSiteBackups(true);
    } catch (e) {
      setSiteBackupMsg('Erreur chargement : ' + (e.response?.data?.error || e.message));
    } finally {
      setSiteBackupLoading(false);
    }
  };

  const createSiteBackup = async (site) => {
    setSiteBackupLoading(true);
    setSiteBackupMsg('');
    try {
      const res = await ax.post('/api/backup', site ? { site } : {});
      setSiteBackups(prev => [...res.data, ...prev]);
      setShowSiteBackups(true);
      setSiteBackupMsg(site ? `Backup de ${site} créé.` : `${res.data.length} backup(s) créés.`);
      setTimeout(() => setSiteBackupMsg(''), 3000);
    } catch (e) {
      setSiteBackupMsg('Erreur : ' + (e.response?.data?.error || e.message));
    } finally {
      setSiteBackupLoading(false);
    }
  };

  const restoreSiteBackup = async (id, site) => {
    if (!window.confirm(`Restaurer ${site} ?\n\nSeul le dossier ${site} sera remplacé — les autres sites ne sont pas touchés.\nUn backup automatique de l'état actuel sera créé avant la restauration.`)) return;
    setSiteBackupLoading(true);
    setSiteBackupMsg('Restauration en cours...');
    try {
      const res = await ax.post(`/api/backup/${encodeURIComponent(id)}/restore`);
      setSiteBackupMsg(`${site} restauré. Backup auto créé : ${res.data.autoBackup}`);
      await loadSiteBackups();
      setTimeout(() => setSiteBackupMsg(''), 7000);
    } catch (e) {
      setSiteBackupMsg('Erreur restauration : ' + (e.response?.data?.error || e.message));
    } finally {
      setSiteBackupLoading(false);
    }
  };

  const downloadSiteBackup = async (id) => {
    try {
      const res = await ax.get(`/api/backup/${encodeURIComponent(id)}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = id; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setSiteBackupMsg('Erreur téléchargement : ' + e.message);
    }
  };

  const deleteSiteBackup = async (id) => {
    if (!window.confirm('Supprimer ce backup ?')) return;
    try {
      await ax.delete(`/api/backup/${encodeURIComponent(id)}`);
      setSiteBackups(prev => prev.filter(b => b.id !== id));
    } catch (e) {
      setSiteBackupMsg('Erreur suppression : ' + (e.response?.data?.error || e.message));
    }
  };

  const fmtSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / 1048576).toFixed(2) + ' Mo';
  };

  const saveProject = async () => {
    if (!pModal?.name) return;
    try {
      pModal.id ? await ax.put(`/api/projects/${pModal.id}`, pModal) : await ax.post('/api/projects', pModal);
      setPModal(null);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };
  const delProject = async (id) => { await ax.delete(`/api/projects/${id}`); };

  const markRead = async (msg) => {
    if (!msg.read) await ax.put(`/api/messages/${msg.id}/read`);
    setSelMsg(msg);
  };

  const delMessage = async (id) => {
    await ax.delete(`/api/messages/${id}`);
    if (selMsg?.id === id) setSelMsg(null);
  };

  const saveInvoice = async () => {
    if (!iModal?.clientName) { alert('Le nom du client est requis.'); return; }
    const lines = (iModal.lines || []).filter(l => l.desc && parseFloat(l.unitPrice) > 0);
    if (lines.length === 0) { alert('Ajoutez au moins une ligne avec un prix.'); return; }
    const subtotal = lines.reduce((s,l) => s+(parseFloat(l.qty)||1)*(parseFloat(l.unitPrice)||0), 0);
    const tps = iModal.taxable ? subtotal*0.05 : 0;
    const tvq = iModal.taxable ? subtotal*0.09975 : 0;
    const total = subtotal + tps + tvq;
    const payload = { ...iModal, lines, subtotal, tps, tvq, total, amount: total };
    try {
      iModal.id
        ? await ax.put(`/api/invoices/${iModal.id}`, payload)
        : await ax.post('/api/invoices', payload);
      setIModal(null);
    } catch (e) {
      console.error('[saveInvoice]', e);
      alert(e.response?.data?.error || e.message || 'Erreur lors de la sauvegarde.');
    }
  };
  const delInvoice = async (id) => { await ax.delete(`/api/invoices/${id}`); };

  // ── FILTERED ──
  const filteredClients = clients.filter(c => {
    const q = cSearch.toLowerCase();
    return (cStage === 'all' || c.status === cStage) &&
      (!q || c.name?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q));
  });

  const filteredWebsites = websites.filter(w => {
    const q = wSearch.toLowerCase();
    return (wStatus === 'all' || w.status === wStatus) &&
      (!q || w.name?.toLowerCase().includes(q) || w.client?.toLowerCase().includes(q) || w.url?.toLowerCase().includes(q));
  });

  const today1y = () => {
    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  };

  const NAV = [
    { sec:15, label:'Tâches',         icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>, badge: null },
    { sec:17, label:'Items',          icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>, badge: null },
    { sec:0,  label:'Commande',       icon:I.cmd,     badge:null },
    { sec:1,  label:'Clients',        icon:I.clients, badge:clients.length || null },
    { sec:2,  label:'Sites web',      icon:I.globe,   badge:websites.filter(w=>w.status==='en_dev').length || null },
    { sec:3,  label:'Projets',        icon:I.folder,  badge:stats.activeProjects || null },
    { sec:4,  label:'Messages',       icon:I.msg,     badge:stats.unread || null },
    { sec:5,  label:'Facturation',    icon:dollar,    badge:stats.enRetard || null },
    { sec:6,  label:'Contrats IA',    icon:I.contract,badge:null, paid:true },
    { sec:7,  label:'Prospection',    icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/></svg>, badge:null, paid:true },
    { sec:18, label:'Prospect. Libre',icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/></svg>, badge:null, paid:true },
    { sec:8,  label:'SEO',            icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>, badge:null, paid:true },
    { sec:16, label:'Fiche Google',   icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>, badge:null, paid:true },
    { sec:9,  label:'Analytics',      icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>, badge:null },
    { sec:10, label:'Préférences',    icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>, badge:null },
    { sec:11, label:'Accès',          icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, badge:null },
    { sec:12, label:'Légal IA',       icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>, badge:null, paid:true },
    { sec:13, label:'Audits SEO',     icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>, badge: audits.length || null, paid:true },
    { sec:14, label:'Mail Marketing', icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>, badge:null, paid:true },
    { sec:19, label:'Devis',          icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>, badge:null },
    { sec:20, label:'Domaine',        icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>, badge:null },
    { sec:21, label:'QR Code',        icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="3" width="5" height="5" rx="1"/><rect x="16" y="3" width="5" height="5" rx="1"/><rect x="3" y="16" width="5" height="5" rx="1"/><path d="M21 16h-5v5M16 16v-5h5"/></svg>, badge:null },
    { sec:22, label:'Palette',        icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v.01M16 10v.01M16 14v.01M12 16v.01M8 14v.01M8 10v.01"/></svg>, badge:null },
    { sec:23, label:'Thème',          icon:<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 2a5 5 0 0 1 5 5c0 2.76-2.24 5-5 5s-5-2.24-5-5a5 5 0 0 1 5-5z"/><path d="M12 17c-3.87 0-7 1.34-7 3v1h14v-1c0-1.66-3.13-3-7-3z"/></svg>, badge:null },
    { sec:24, label:'Vybme Admin',    icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF1F6A" strokeWidth="1.8"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>, badge: vibeeReportCount || null },
    { sec:27, label:'Vybme Revenus',  icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>, badge: null },
    { sec:28, label:'Vybme Visiteurs',icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#38BDF8" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, badge: null },
    { sec:29, label:'QS Usagers',     icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E5341A" strokeWidth="1.8"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>, badge: null },
    { sec:25, label:'Graph View',     icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>, badge: null },
  ];

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#060608', fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif', flexDirection: isMobile ? 'column' : 'row' }}>
      <style>{`
        :root{--accent:${accent};--accent-bg:${accent}18;--accent-border:${accent}44;--accent-glow:${accent}66}
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes glow{0%,100%{opacity:1}50%{opacity:0.5}}
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.08);border-radius:99px}
        input[type=range]{-webkit-appearance:none;height:4px;background:rgba(255,255,255,0.08);border-radius:99px;outline:none}
        input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;border-radius:50%;background:var(--accent);cursor:pointer;box-shadow:0 0 8px var(--accent-glow)}
      `}</style>

      {/* ══ SIDEBAR (desktop) / TOP BAR (mobile) ══ */}
      {isMobile ? (
        <header style={{ position:'sticky', top:0, zIndex:100, background:'#0a0a0d', borderBottom:'1px solid rgba(255,255,255,0.06)', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:24, height:24, background:'var(--accent,#C8FF00)', borderRadius:5, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, color:'#000', fontSize:'0.75rem' }}>U</div>
            <div style={{ fontWeight:800, color:'#EAEAEA', fontSize:'0.88rem', letterSpacing:'-0.02em' }}>creative</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Avatar name={currentUser?.displayName || currentUser?.email || ''} size={28}/>
            <button onClick={handleLogout} style={{ background:'none', border:'1px solid rgba(255,255,255,0.08)', borderRadius:7, padding:'5px 10px', color:'#555', cursor:'pointer', fontSize:'0.75rem', fontWeight:600 }}>Sortir</button>
          </div>
        </header>
      ) : (
      <aside style={{ width:220, background:'#0a0a0d', borderRight:'1px solid rgba(255,255,255,0.05)', padding:'24px 12px', display:'flex', flexDirection:'column', flexShrink:0, position:'sticky', top:0, height:'100vh', overflowY:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 8px', marginBottom:28 }}>
          <div style={{ width:26, height:26, background:'var(--accent,#C8FF00)', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, color:'#000', fontSize:'0.8rem', letterSpacing:'-0.04em' }}>U</div>
          <div>
            <div style={{ fontWeight:800, color:'#EAEAEA', fontSize:'0.9rem', letterSpacing:'-0.02em', lineHeight:1 }}>creative</div>
          </div>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:9, padding:'10px', background:'rgba(255,255,255,0.03)', borderRadius:10, marginBottom:24, border:'1px solid rgba(255,255,255,0.05)' }}>
          <Avatar name={currentUser?.displayName || currentUser?.email || ''} size={32}/>
          <div style={{ overflow:'hidden', flex:1 }}>
            <div style={{ fontWeight:700, color:'#EAEAEA', fontSize:'0.8rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{currentUser?.displayName || currentUser?.email?.split('@')[0]}</div>
            <div style={{ fontSize:'0.65rem', color:'#333', fontWeight:600, marginTop:1 }}>{userRole === 'admin' ? '⚡ Admin' : 'Client'}</div>
          </div>
        </div>

        <nav style={{ display:'flex', flexDirection:'column', gap:2, flex:1 }}>
          {NAV.map((item) => (
            <button key={item.sec} onClick={() => setSec(item.sec)} style={{ display:'flex', alignItems:'center', gap:9, padding:'9px 10px', borderRadius:8, border:'none', background: sec===item.sec ? 'var(--accent-bg,rgba(200,255,0,0.07))' : 'transparent', color: sec===item.sec ? 'var(--accent,#C8FF00)' : '#444', cursor:'pointer', fontSize:'0.84rem', fontWeight:600, width:'100%', textAlign:'left', transition:'all 0.12s' }}
              onMouseEnter={e=>{ if(sec!==item.sec){e.currentTarget.style.background='rgba(255,255,255,0.03)'; e.currentTarget.style.color='#888'}}}
              onMouseLeave={e=>{ if(sec!==item.sec){e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#444'}}}>
              <span style={{ opacity: sec===item.sec ? 1 : 0.6 }}>{item.icon}</span>
              <span style={{ flex:1 }}>{item.label}</span>
              {item.paid && <span style={{ fontSize:'0.6rem', color:'#f59e0b', opacity: sec===item.sec ? 1 : 0.5 }} title="Utilise un service payant">★</span>}
              {item.badge > 0 && <span style={{ fontSize:'0.65rem', fontWeight:800, padding:'1px 6px', borderRadius:99, background: sec===item.sec ? 'var(--accent-bg,rgba(200,255,0,0.2))' : 'rgba(255,255,255,0.06)', color: sec===item.sec ? 'var(--accent,#C8FF00)' : '#555' }}>{item.badge}</span>}
            </button>
          ))}
        </nav>

        <button onClick={handleLogout} style={{ display:'flex', alignItems:'center', gap:9, padding:'9px 10px', borderRadius:8, border:'none', background:'transparent', color:'#333', cursor:'pointer', fontSize:'0.82rem', fontWeight:600, width:'100%', marginTop:12, transition:'color 0.12s' }}
          onMouseEnter={e=>e.currentTarget.style.color='#f87171'}
          onMouseLeave={e=>e.currentTarget.style.color='#333'}>
          {I.out} Déconnexion
        </button>
      </aside>
      )}

      {/* ══ MAIN ══ */}
      <main style={{ flex:1, overflowY:'auto', padding: isMobile ? '20px 16px 80px' : '44px 52px', animation:'fadeIn 0.25s ease' }}>

        {/* ════ 0 — COMMANDE ════ */}
        {sec === 0 && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end', marginBottom: isMobile ? 24 : 40, flexWrap:'wrap', gap:12 }}>
              <div>
                <div style={{ fontSize:'0.7rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Centre de commande</div>
                <h1 style={{ fontSize: isMobile ? '1.6rem' : '2.2rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.04em', lineHeight:1 }}>Ucreative</h1>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {!isMobile && <button style={btn.ghost} onClick={() => { setSec(1); setCModal({...EMPTY_CLIENT}); }}>{I.plus} Client</button>}
                <button style={btn.primary} onClick={() => { setSec(2); setWModal({...EMPTY_WEBSITE}); }}>{I.plus} Nouveau site</button>
              </div>
            </div>

            {/* KPI CARDS */}
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 10 : 14, marginBottom:32 }}>
              {[
                { label:'Clients actifs',   val: stats.activeClients,    sub:`sur ${clients.length} total`, accent:true },
                { label:'Sites en ligne',   val: stats.sitesLive,        sub:`sur ${websites.length} sites` },
                { label:'Projets actifs',   val: stats.activeProjects,   sub:`${projects.length} au total` },
                { label:'Budget total',     val: `$${(stats.budget/1000).toFixed(0)}k`, sub:'tous clients' },
              ].map(k => (
                <div key={k.label} style={{ background: k.accent ? 'var(--accent,#C8FF00)' : '#0e0e12', border: k.accent ? 'none' : '1px solid rgba(255,255,255,0.05)', borderRadius:12, padding:'22px 20px' }}>
                  <div style={{ fontSize:'0.66rem', color: k.accent?'rgba(0,0,0,0.5)':'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:12 }}>{k.label}</div>
                  <div style={{ fontSize:'2.6rem', fontWeight:900, color: k.accent?'#000':'#EAEAEA', letterSpacing:'-0.04em', lineHeight:1, fontVariantNumeric:'tabular-nums' }}>{k.val}</div>
                  <div style={{ fontSize:'0.72rem', color: k.accent?'rgba(0,0,0,0.4)':'#333', marginTop:6, fontWeight:500 }}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* KPI FINANCIERS */}
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 10 : 14, marginBottom:32 }}>
              {[
                { label:'Total facturé',   val:`$${stats.totalFacturé.toLocaleString('fr-CA',{minimumFractionDigits:0,maximumFractionDigits:0})}`, sub:`${invoices.length} facture${invoices.length!==1?'s':''}`, color:'#60a5fa' },
                { label:'Montant encaissé',val:`$${stats.totalPayé.toLocaleString('fr-CA',{minimumFractionDigits:0,maximumFractionDigits:0})}`,   sub:invoices.length ? `${Math.round(stats.totalPayé/(stats.totalFacturé||1)*100)}% du total` : '—', color:'#4ade80' },
                { label:'En attente',      val:`$${stats.enAttente.toLocaleString('fr-CA',{minimumFractionDigits:0,maximumFractionDigits:0})}`,    sub:`${invoices.filter(i=>i.status==='en_attente').length} facture${invoices.filter(i=>i.status==='en_attente').length!==1?'s':''}`, color:'#fbbf24' },
                { label:'Messages non lus',val:stats.unread, sub:`sur ${messages.length} message${messages.length!==1?'s':''}`, color:stats.unread>0?'#f87171':'#444' },
              ].map(k => (
                <div key={k.label} style={{ background:'#0e0e12', border:`1px solid ${k.color}18`, borderRadius:12, padding:'22px 20px', position:'relative', overflow:'hidden' }}>
                  <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:k.color, opacity:0.5, borderRadius:'12px 12px 0 0' }}/>
                  <div style={{ fontSize:'0.66rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:12 }}>{k.label}</div>
                  <div style={{ fontSize:'2.6rem', fontWeight:900, color:k.color, letterSpacing:'-0.04em', lineHeight:1, fontVariantNumeric:'tabular-nums' }}>{k.val}</div>
                  <div style={{ fontSize:'0.72rem', color:'#333', marginTop:6, fontWeight:500 }}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* WELCOME CHECKLIST */}
            {!checklistHidden && (() => {
              const items = [
                { label: 'Premier client ajouté',     done: clients.length > 0 },
                { label: 'Premier site créé',         done: websites.length > 0 },
                { label: 'Premier projet enregistré', done: projects.length > 0 },
              ];
              if (items.every(i => i.done)) return null;
              return (
                <div style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, padding:'20px 24px', marginBottom:24, display:'flex', gap:24, alignItems:'flex-start', flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:220 }}>
                    <div style={{ fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'#444', marginBottom:10 }}>Démarrage</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {items.map(it => (
                        <div key={it.label} style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:18, height:18, borderRadius:'50%', flexShrink:0, background: it.done ? accent : 'transparent', border: it.done ? 'none' : '1.5px solid rgba(255,255,255,0.12)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {it.done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#080808" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
                          </div>
                          <span style={{ fontSize:'0.83rem', color: it.done ? '#888' : '#EAEAEA', textDecoration: it.done ? 'line-through' : 'none', fontWeight: it.done ? 400 : 600 }}>{it.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button onClick={() => { localStorage.setItem('checklist-dismissed','1'); setChecklistHidden(true); }} style={{ background:'transparent', border:'none', color:'#333', fontSize:'0.75rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.06em', textTransform:'uppercase', alignSelf:'flex-start', padding:'4px 0' }}>Masquer</button>
                </div>
              );
            })()}

            {/* BOTTOM 2-COL */}
            <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:16 }}>
              {/* Pipeline rapide */}
              <div style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, padding:24 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                  <span style={{ fontWeight:700, color:'#EAEAEA', fontSize:'0.9rem' }}>Pipeline clients</span>
                  <button style={btn.ghost} onClick={()=>setSec(1)}>Voir tout {I.arrow}</button>
                </div>
                {Object.entries(CLIENT_STAGES).filter(([k])=>k!=='inactive').map(([k,v]) => (
                  <div key={k} style={{ display:'flex', alignItems:'center', gap:12, padding:'9px 0', borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                    <Dot color={v.color} glow={v.color}/>
                    <span style={{ flex:1, fontSize:'0.84rem', color:'#888', fontWeight:500 }}>{v.label}</span>
                    <span style={{ fontWeight:800, color:'#EAEAEA', fontSize:'1rem', fontVariantNumeric:'tabular-nums' }}>{stageCount(k)}</span>
                    <div style={{ width:60 }}><Bar value={clients.length ? Math.round(stageCount(k)/clients.length*100) : 0} color={v.color} height={3}/></div>
                  </div>
                ))}
              </div>

              {/* Sites récents */}
              <div style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, padding:24 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                  <span style={{ fontWeight:700, color:'#EAEAEA', fontSize:'0.9rem' }}>Sites récents</span>
                  <button style={btn.ghost} onClick={()=>setSec(2)}>Voir tout {I.arrow}</button>
                </div>
                {websites.slice(0,5).map(w => {
                  const ws = WEB_STATUS[w.status] || WEB_STATUS.pause;
                  return (
                    <div key={w.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                      <Dot color={ws.color} glow={ws.glow}/>
                      <div style={{ flex:1, overflow:'hidden' }}>
                        <div style={{ fontWeight:600, color:'#EAEAEA', fontSize:'0.84rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{w.name}</div>
                        <div style={{ color:'#333', fontSize:'0.72rem' }}>{w.client || '—'}</div>
                      </div>
                      <div style={{ minWidth:50 }}><Bar value={w.progress||0} color={ws.color} height={3}/></div>
                    </div>
                  );
                })}
                {websites.length === 0 && <div style={{ color:'#333', fontSize:'0.84rem', textAlign:'center', padding:'20px 0' }}>Aucun site pour l'instant</div>}
              </div>
            </div>
          </div>
        )}

        {/* ════ 1 — CLIENTS CRM ════ */}
        {sec === 1 && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:32 }}>
              <div>
                <div style={{ fontSize:'0.7rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>CRM</div>
                <h1 style={{ fontSize:'2rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.04em' }}>Clients</h1>
              </div>
              <button style={btn.primary} onClick={()=>setCModal({...EMPTY_CLIENT})}>{I.plus} Nouveau client</button>
            </div>

            {/* PIPELINE BAR */}
            <div style={{ display:'flex', gap:6, marginBottom:20, overflowX:'auto', paddingBottom:4 }}>
              <button style={{ ...filterPill(cStage==='all'), flexShrink:0 }} onClick={()=>setCStage('all')}>
                Tous <span style={pillCount}>{clients.length}</span>
              </button>
              {Object.entries(CLIENT_STAGES).filter(([k])=>k!=='inactive').map(([k,v])=>(
                <button key={k} style={{ ...filterPill(cStage===k), flexShrink:0, color: cStage===k ? v.color : '#444', borderColor: cStage===k ? v.color+'55' : 'rgba(255,255,255,0.07)', background: cStage===k ? v.bg : 'transparent' }} onClick={()=>setCStage(k)}>
                  {v.label} <span style={pillCount}>{stageCount(k)}</span>
                </button>
              ))}
            </div>

            {/* SEARCH */}
            <div style={{ position:'relative', marginBottom:20, maxWidth:360 }}>
              <span style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'#333', pointerEvents:'none' }}>{I.search}</span>
              <input style={{ width:'100%', background:'#0e0e12', border:'1px solid rgba(255,255,255,0.07)', borderRadius:9, padding:'9px 12px 9px 34px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none' }}
                placeholder="Rechercher un client..." value={cSearch} onChange={e=>setCSearch(e.target.value)}/>
            </div>

            {/* CLIENT GRID */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))', gap:14 }}>
              {filteredClients.map(c => {
                const stage = CLIENT_STAGES[c.status] || CLIENT_STAGES.inactive;
                return (
                  <div key={c.id} onClick={()=>setSelClient(c)} style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, padding:20, cursor:'pointer', transition:'border-color 0.15s', position:'relative' }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(255,255,255,0.12)'}
                    onMouseLeave={e=>e.currentTarget.style.borderColor='rgba(255,255,255,0.05)'}>
                    <div style={{ position:'absolute', top:0, left:0, right:0, height:2, borderRadius:'12px 12px 0 0', background:stage.color, opacity:0.5 }}/>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
                      <Avatar name={c.name} size={38}/>
                      <Badge status={c.status}/>
                    </div>
                    <div style={{ fontWeight:700, color:'#EAEAEA', fontSize:'0.95rem', marginBottom:2 }}>{c.name}</div>
                    <div style={{ color:'#444', fontSize:'0.78rem', marginBottom:14 }}>{c.company || c.email}</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, paddingTop:14, borderTop:'1px solid rgba(255,255,255,0.04)' }}>
                      <div>
                        <div style={{ fontSize:'0.62rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:3 }}>Projet</div>
                        <div style={{ fontSize:'0.78rem', color:'#888' }}>{c.project || '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:'0.62rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:3 }}>Budget</div>
                        <div style={{ fontSize:'0.78rem', color:'#888', fontVariantNumeric:'tabular-nums' }}>{c.budget ? `$${parseFloat(c.budget).toLocaleString()}` : '—'}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {filteredClients.length === 0 && <div style={{ color:'#333', fontSize:'0.88rem', textAlign:'center', padding:'60px 0' }}>Aucun client trouvé</div>}
          </div>
        )}

        {/* ════ 2 — SITES WEB ════ */}
        {sec === 2 && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:32 }}>
              <div>
                <div style={{ fontSize:'0.7rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Fabrication</div>
                <h1 style={{ fontSize:'2rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.04em' }}>Sites web</h1>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <button style={{ ...btn.ghost, fontSize:'0.78rem', padding:'7px 14px', display:'flex', alignItems:'center', gap:6 }}
                  onClick={() => showSiteBackups ? setShowSiteBackups(false) : loadSiteBackups()}
                  disabled={siteBackupLoading}>
                  <span style={{ fontSize:13 }}>🗂</span>
                  {siteBackupLoading ? 'Chargement...' : showSiteBackups ? 'Masquer backups' : 'Backups sources'}
                </button>
                <button style={{ ...btn.ghost, fontSize:'0.78rem', padding:'7px 14px', display:'flex', alignItems:'center', gap:6, color:'#4ade80', borderColor:'rgba(74,222,128,0.25)' }}
                  onClick={() => createSiteBackup()}
                  disabled={siteBackupLoading}>
                  <span style={{ fontSize:13 }}>⬆</span>
                  {siteBackupLoading ? 'En cours...' : 'Sauvegarder tout'}
                </button>
                <button style={{ ...btn.primary, background:'linear-gradient(135deg,#C8FF00,#a0d600)', color:'#000', border:'none', fontWeight:800, letterSpacing:'0.03em' }} onClick={openAiFactory}>
                  {I.spark} Générer avec IA
                </button>
                <button style={btn.primary} onClick={()=>setWModal({...EMPTY_WEBSITE})}>{I.plus} Nouveau site</button>
              </div>
            </div>

            {/* BACKUP SOURCES PANEL */}
            {showSiteBackups && (
              <div style={{ background:'#0d0d0d', border:'1px solid rgba(255,255,255,0.07)', borderRadius:12, padding:16, marginBottom:24 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                  <div style={{ fontSize:'0.68rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' }}>Backups — client/src/sites/</div>
                  <div style={{ fontSize:'0.72rem', color:'#333' }}>{siteBackups.length} backup(s)</div>
                </div>
                {siteBackupMsg && (
                  <div style={{ fontSize:'0.8rem', color: siteBackupMsg.includes('Erreur') ? '#f87171' : '#4ade80', background: siteBackupMsg.includes('Erreur') ? 'rgba(248,113,113,0.07)' : 'rgba(74,222,128,0.07)', border:`1px solid ${siteBackupMsg.includes('Erreur')?'rgba(248,113,113,0.2)':'rgba(74,222,128,0.2)'}`, borderRadius:8, padding:'8px 12px', marginBottom:12 }}>
                    {siteBackupMsg}
                  </div>
                )}
                {siteBackups.length === 0 ? (
                  <div style={{ fontSize:'0.82rem', color:'#333', textAlign:'center', padding:'20px 0' }}>
                    Aucun backup — cliquez "Sauvegarder tout" pour créer le premier.
                  </div>
                ) : (() => {
                  const grouped = siteBackups.reduce((acc, b) => {
                    if (!acc[b.site]) acc[b.site] = [];
                    acc[b.site].push(b);
                    return acc;
                  }, {});
                  return (
                    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                      {Object.entries(grouped).sort(([a],[b])=>a.localeCompare(b)).map(([site, backups]) => (
                        <div key={site}>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <div style={{ background:'rgba(245,166,35,0.12)', border:'1px solid rgba(245,166,35,0.25)', borderRadius:5, padding:'2px 8px', fontSize:'0.7rem', color:'#F5A623', fontWeight:700, letterSpacing:'0.05em' }}>{site}</div>
                              <span style={{ fontSize:'0.7rem', color:'#333' }}>{backups.length} backup(s)</span>
                            </div>
                            <button onClick={() => createSiteBackup(site)} disabled={siteBackupLoading}
                              style={{ background:'transparent', border:'1px solid rgba(74,222,128,0.2)', color:'#4ade80', padding:'3px 10px', borderRadius:6, cursor:'pointer', fontSize:'0.7rem', fontWeight:600 }}>
                              + Sauvegarder
                            </button>
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                            {backups.map(b => {
                              const d = new Date(b.createdAt);
                              const dateStr = d.toLocaleDateString('fr-CA', { year:'numeric', month:'short', day:'numeric' });
                              const timeStr = d.toLocaleTimeString('fr-CA', { hour:'2-digit', minute:'2-digit' });
                              const isAuto  = b.id.includes('-auto-');
                              return (
                                <div key={b.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background: isAuto ? '#0e0e10' : '#111', border:`1px solid ${isAuto ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.07)'}`, borderRadius:8, padding:'9px 12px' }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                    <span style={{ fontSize:16 }}>{isAuto ? '🔄' : '📦'}</span>
                                    <div>
                                      <div style={{ fontSize:'0.78rem', color: isAuto ? '#555' : '#EAEAEA', fontWeight:600 }}>
                                        {dateStr} à {timeStr}
                                        {isAuto && <span style={{ marginLeft:6, fontSize:'0.68rem', color:'#333' }}>auto</span>}
                                      </div>
                                      <div style={{ fontSize:'0.7rem', color:'#333', marginTop:1 }}>{fmtSize(b.size)}</div>
                                    </div>
                                  </div>
                                  <div style={{ display:'flex', gap:5 }}>
                                    <button onClick={() => downloadSiteBackup(b.id)}
                                      style={{ background:'rgba(99,179,237,0.08)', border:'1px solid rgba(99,179,237,0.2)', color:'#63b3ed', padding:'4px 10px', borderRadius:6, cursor:'pointer', fontSize:'0.72rem', fontWeight:600 }}>
                                      ⬇ Télécharger
                                    </button>
                                    <button onClick={() => restoreSiteBackup(b.id, site)} disabled={siteBackupLoading}
                                      style={{ background:'rgba(245,166,35,0.08)', border:'1px solid rgba(245,166,35,0.22)', color:'#F5A623', padding:'4px 10px', borderRadius:6, cursor:'pointer', fontSize:'0.72rem', fontWeight:700 }}>
                                      Restaurer
                                    </button>
                                    <button onClick={() => deleteSiteBackup(b.id)} disabled={siteBackupLoading}
                                      style={{ background:'transparent', border:'1px solid rgba(248,113,113,0.15)', color:'#f87171', padding:'4px 8px', borderRadius:6, cursor:'pointer', fontSize:'0.72rem' }}>
                                      ✕
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* STATUS FILTER PILLS */}
            <div style={{ display:'flex', gap:6, marginBottom:20, overflowX:'auto', paddingBottom:4 }}>
              <button style={filterPill(wStatus==='all')} onClick={()=>setWStatus('all')}>
                Tous <span style={pillCount}>{websites.length}</span>
              </button>
              {Object.entries(WEB_STATUS).map(([k,v])=>(
                <button key={k} style={{ ...filterPill(wStatus===k), flexShrink:0, color: wStatus===k ? v.color : '#444', borderColor: wStatus===k ? v.border : 'rgba(255,255,255,0.07)', background: wStatus===k ? v.glow.replace('0.25','0.08') : 'transparent' }} onClick={()=>setWStatus(k)}>
                  <Dot color={v.color} glow={v.glow}/> {v.label}
                  <span style={pillCount}>{websites.filter(w=>w.status===k).length}</span>
                </button>
              ))}
            </div>

            {/* SEARCH */}
            <div style={{ position:'relative', marginBottom:24, maxWidth:360 }}>
              <span style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'#333', pointerEvents:'none' }}>{I.search}</span>
              <input style={{ width:'100%', background:'#0e0e12', border:'1px solid rgba(255,255,255,0.07)', borderRadius:9, padding:'9px 12px 9px 34px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none' }}
                placeholder="Rechercher un site..." value={wSearch} onChange={e=>setWSearch(e.target.value)}/>
            </div>

            {/* SITE CARDS GRID */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(310px,1fr))', gap:16 }}>
              {filteredWebsites.map(w => {
                const ws = WEB_STATUS[w.status] || WEB_STATUS.pause;
                const progColor = w.status==='en_ligne' ? '#4ade80' : w.status==='en_test' ? '#60a5fa' : '#C8FF00';
                return (
                  <div key={w.id} style={{ background:'#0e0e12', border:`1px solid ${ws.border}`, borderRadius:14, padding:22, transition:'box-shadow 0.2s', position:'relative', overflow:'hidden' }}
                    onMouseEnter={e=>e.currentTarget.style.boxShadow=`0 0 24px ${ws.glow}`}
                    onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>

                    {/* Top glow strip */}
                    <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:`linear-gradient(90deg, transparent, ${ws.color}, transparent)`, opacity:0.6 }}/>

                    {/* Header */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <Dot color={ws.color} glow={ws.glow}/>
                        <span style={{ fontSize:'0.68rem', color:ws.color, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>{ws.label}</span>
                      </div>
                      {w.techStack && (
                        <span style={{ fontSize:'0.65rem', fontWeight:700, color:'#555', background:'rgba(255,255,255,0.05)', padding:'3px 8px', borderRadius:5, letterSpacing:'0.04em' }}>{w.techStack}</span>
                      )}
                    </div>

                    {/* Site name */}
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:3 }}>
                      {w.logoUrl && <img src={w.logoUrl} alt="" style={{ height:28, maxWidth:60, objectFit:'contain', borderRadius:4 }} onError={e=>e.target.style.display='none'}/>}
                      <div style={{ fontWeight:800, color:'#EAEAEA', fontSize:'1.1rem', letterSpacing:'-0.02em' }}>{w.name}</div>
                    </div>
                    <div style={{ color:'#444', fontSize:'0.78rem', marginBottom:12 }}>{w.client || 'Client non assigné'}</div>

                    {/* URLs */}
                    <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:16 }}>
                      {w.identifier && (
                        <a href={`http://localhost:3000/${w.identifier}`} target="_blank" rel="noreferrer"
                          style={{ display:'inline-flex', alignItems:'center', gap:5, color:'#C8FF00', fontSize:'0.73rem', textDecoration:'none', fontWeight:700 }}
                          onClick={e=>e.stopPropagation()}>
                          {I.globe} localhost:3000/{w.identifier}
                        </a>
                      )}
                      {w.url && (
                        <a href={w.url.startsWith('http') ? w.url : `https://${w.url}`} target="_blank" rel="noreferrer"
                          style={{ display:'inline-flex', alignItems:'center', gap:5, color:'#60a5fa', fontSize:'0.73rem', textDecoration:'none', fontWeight:600 }}
                          onClick={e=>e.stopPropagation()}>
                          {I.link} {w.url.replace(/^https?:\/\//,'')}
                        </a>
                      )}
                    </div>

                    {/* Progress */}
                    <div style={{ marginBottom:16 }}>
                      <Bar value={w.progress||0} color={progColor} height={5}/>
                    </div>

                    {/* Meta grid */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, paddingTop:14, borderTop:'1px solid rgba(255,255,255,0.04)', marginBottom:16 }}>
                      <div>
                        <div style={{ fontSize:'0.6rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:3 }}>Budget</div>
                        <div style={{ fontSize:'0.8rem', color:'#888', fontVariantNumeric:'tabular-nums' }}>{w.budget ? `$${parseFloat(w.budget).toLocaleString()}` : '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:'0.6rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:3 }}>Échéance</div>
                        <div style={{ fontSize:'0.8rem', color:'#888' }}>{w.dueDate || '—'}</div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      <IconBtn icon={I.pen} onClick={()=>setWModal({...w})}/>
                      <IconBtn icon={I.bin} onClick={()=>setDelTarget({...w, _type:'website'})} danger/>
                      {w.identifier && (
                        <button onClick={()=>openSiteEditor(w)}
                          style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(200,255,0,0.08)', color:'#C8FF00', border:'1px solid rgba(200,255,0,0.25)', padding:'5px 10px', borderRadius:7, fontWeight:700, fontSize:'0.72rem', cursor:'pointer', letterSpacing:'0.02em' }}>
                          ✎ Contenu
                        </button>
                      )}
                      {w.identifier && (
                        <button onClick={()=>navigate(`/editor/${w.identifier}`)}
                          style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(255,107,43,0.1)', color:'#FF6B2B', border:'1px solid rgba(255,107,43,0.3)', padding:'5px 10px', borderRadius:7, fontWeight:700, fontSize:'0.72rem', cursor:'pointer', letterSpacing:'0.02em' }}>
                          ◎ Éditeur
                        </button>
                      )}
                      {w.identifier && (
                        <button onClick={()=>{ setFtpModal({target:w.identifier,name:w.name}); setFtpLog([]); setFtpRunning(false); }}
                          style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(96,165,250,0.08)', color:'#60a5fa', border:'1px solid rgba(96,165,250,0.25)', padding:'5px 10px', borderRadius:7, fontWeight:700, fontSize:'0.72rem', cursor:'pointer', letterSpacing:'0.02em' }}>
                          ↑ FTP
                        </button>
                      )}
                      {w.identifier && (
                        <button onClick={()=>{ setFbModal({target:w.identifier,name:w.name}); setFbLog([]); setFbRunning(false); setFbDoneUrl(null); }}
                          style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(255,160,0,0.08)', color:'#FFAB00', border:'1px solid rgba(255,160,0,0.25)', padding:'5px 10px', borderRadius:7, fontWeight:700, fontSize:'0.72rem', cursor:'pointer', letterSpacing:'0.02em' }}>
                          🔥 Firebase
                        </button>
                      )}
                      {w.url && (
                        <a href={w.url.startsWith('http')?w.url:`https://${w.url}`} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
                          style={{ display:'inline-flex', alignItems:'center', gap:5, marginLeft:'auto', ...btn.outline, textDecoration:'none' }}>
                          {I.link} Visiter
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {filteredWebsites.length === 0 && <div style={{ color:'#333', fontSize:'0.88rem', textAlign:'center', padding:'60px 0' }}>Aucun site trouvé</div>}
          </div>
        )}

        {/* ════ 3 — PROJETS ════ */}
        {sec === 3 && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:32 }}>
              <div>
                <div style={{ fontSize:'0.7rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Suivi</div>
                <h1 style={{ fontSize:'2rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.04em' }}>Projets</h1>
              </div>
              <button style={btn.primary} onClick={()=>setPModal({...EMPTY_PROJECT})}>{I.plus} Nouveau projet</button>
            </div>
            <div style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    {['Projet','Client','Avancement','Échéance','Statut',''].map(h=>(
                      <th key={h} style={{ padding:'13px 16px', textAlign:'left', fontSize:'0.65rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {projects.map(p => (
                    <tr key={p.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.03)', transition:'background 0.1s' }}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={td}><span style={{ fontWeight:700, color:'#EAEAEA' }}>{p.name}</span></td>
                      <td style={td}>{p.client || '—'}</td>
                      <td style={{ ...td, minWidth:160 }}><Bar value={p.progress||0} color={p.progress>=80?'#4ade80':'#C8FF00'}/></td>
                      <td style={td}>{p.dueDate || '—'}</td>
                      <td style={td}><Badge status={p.status}/></td>
                      <td style={td}>
                        <div style={{ display:'flex', gap:5 }}>
                          <IconBtn icon={I.pen} onClick={()=>setPModal({...p})}/>
                          <IconBtn icon={I.bin} onClick={()=>delProject(p.id)} danger/>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {projects.length === 0 && <div style={{ color:'#333', fontSize:'0.88rem', textAlign:'center', padding:'60px 0' }}>Aucun projet pour l'instant</div>}
            </div>
          </div>
        )}

        {/* ════ 4 — MESSAGES ════ */}
        {sec === 4 && (
          <div>
            <div style={{ marginBottom:32 }}>
              <div style={{ fontSize:'0.7rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Boîte de réception</div>
              <h1 style={{ fontSize:'2rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.04em' }}>Messages</h1>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'360px 1fr', gap:16, alignItems:'start' }}>
              <div style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, overflow:'hidden', maxHeight:'72vh', overflowY:'auto' }}>
                {messages.map(m => (
                  <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom:'1px solid rgba(255,255,255,0.03)', background: selMsg?.id===m.id ? 'var(--accent-bg,rgba(200,255,0,0.04))' : 'transparent', transition:'background 0.12s', borderLeft:`2px solid ${!m.read?'var(--accent,#C8FF00)':'transparent'}` }}
                    onMouseEnter={e=>{ if(selMsg?.id!==m.id) e.currentTarget.style.background='rgba(255,255,255,0.02)'; }}
                    onMouseLeave={e=>{ if(selMsg?.id!==m.id) e.currentTarget.style.background='transparent'; }}>
                    <div onClick={()=>markRead(m)} style={{ display:'flex', alignItems:'center', gap:10, flex:1, minWidth:0, cursor:'pointer' }}>
                      <Avatar name={m.from} size={32}/>
                      <div style={{ flex:1, overflow:'hidden' }}>
                        <div style={{ fontWeight: m.read?600:800, color:'#EAEAEA', fontSize:'0.84rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{m.from}</div>
                        <div style={{ color:'#333', fontSize:'0.74rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{m.subject}</div>
                      </div>
                      {!m.read && <div style={{ width:7, height:7, borderRadius:'50%', background:'var(--accent,#C8FF00)', flexShrink:0, boxShadow:'0 0 6px var(--accent-glow,rgba(200,255,0,0.6))' }}/>}
                    </div>
                    <button onClick={e=>{ e.stopPropagation(); delMessage(m.id); }}
                      style={{ flexShrink:0, width:26, height:26, borderRadius:6, border:'1px solid rgba(248,113,113,0.15)', background:'transparent', color:'#f87171', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', opacity:0.5, transition:'opacity 0.15s' }}
                      onMouseEnter={e=>e.currentTarget.style.opacity='1'}
                      onMouseLeave={e=>e.currentTarget.style.opacity='0.5'}>
                      {I.bin}
                    </button>
                  </div>
                ))}
                {messages.length === 0 && <div style={{ color:'#333', fontSize:'0.84rem', textAlign:'center', padding:'40px 0' }}>Aucun message</div>}
              </div>

              <div style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, padding:28, minHeight:300 }}>
                {selMsg ? (
                  <>
                    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24, paddingBottom:20, borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                      <Avatar name={selMsg.from} size={44}/>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:800, color:'#EAEAEA', fontSize:'1rem' }}>{selMsg.from}</div>
                        <div style={{ color:'#444', fontSize:'0.78rem', marginTop:2 }}>{selMsg.email}</div>
                      </div>
                      <button onClick={()=>delMessage(selMsg.id)}
                        style={{ display:'inline-flex', alignItems:'center', gap:6, background:'transparent', color:'#f87171', border:'1px solid rgba(248,113,113,0.25)', borderRadius:8, padding:'7px 12px', fontSize:'0.78rem', fontWeight:700, cursor:'pointer' }}
                        onMouseEnter={e=>{ e.currentTarget.style.background='rgba(248,113,113,0.08)'; e.currentTarget.style.borderColor='rgba(248,113,113,0.5)'; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='rgba(248,113,113,0.25)'; }}>
                        {I.bin} Supprimer
                      </button>
                    </div>
                    <div style={{ fontWeight:700, color:'#EAEAEA', fontSize:'1.05rem', marginBottom:16 }}>{selMsg.subject}</div>
                    <div style={{ color:'#666', lineHeight:1.75, fontSize:'0.88rem', whiteSpace:'pre-wrap' }}>{selMsg.body || 'Aucun contenu.'}</div>
                    <div style={{ marginTop:28, paddingTop:20, borderTop:'1px solid rgba(255,255,255,0.05)' }}>
                      <a href={`mailto:${selMsg.email}?subject=Re: ${selMsg.subject}`} style={{ ...btn.primary, textDecoration:'none', display:'inline-flex' }}>Répondre par courriel</a>
                    </div>
                  </>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:200, color:'#333', fontSize:'0.84rem' }}>Sélectionnez un message</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ════ 5 — FACTURATION ════ */}
        {sec === 5 && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:32 }}>
              <div>
                <div style={{ fontSize:'0.7rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Finance</div>
                <h1 style={{ fontSize:'2rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.04em' }}>Facturation</h1>
              </div>
              <button style={btn.primary} onClick={()=>setIModal({...EMPTY_INVOICE})}>{I.plus} Nouvelle facture</button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:32 }}>
              {[
                { label:'Total facturé', val:`$${stats.totalFacturé.toLocaleString()}`, color:'#EAEAEA', accent:false },
                { label:'Encaissé',      val:`$${stats.totalPayé.toLocaleString()}`,    color:'#4ade80', accent:false },
                { label:'En attente',    val:`$${stats.enAttente.toLocaleString()}`,    color:'#fbbf24', accent:false },
              ].map(k=>(
                <div key={k.label} style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, padding:'20px 22px' }}>
                  <div style={{ fontSize:'0.65rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:10 }}>{k.label}</div>
                  <div style={{ fontSize:'2.2rem', fontWeight:900, color:k.color, letterSpacing:'-0.04em', fontVariantNumeric:'tabular-nums' }}>{k.val}</div>
                </div>
              ))}
            </div>

            <div style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    {['N°','Client','Lignes','Sous-total','Taxes','Total TTC','Échéance','Statut',''].map(h=>(
                      <th key={h} style={{ padding:'13px 16px', textAlign:'left', fontSize:'0.65rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv=>{
                    return (
                      <tr key={inv.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.03)' }}
                        onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={{ ...td, color:'#444', fontVariantNumeric:'tabular-nums', fontWeight:600 }}>{inv.number || '—'}</td>
                        <td style={td}>
                          <div style={{ fontWeight:700, color:'#EAEAEA' }}>{inv.clientName}</div>
                          {inv.clientCompany && <div style={{ fontSize:'0.72rem', color:'#555', marginTop:2 }}>{inv.clientCompany}</div>}
                        </td>
                        <td style={{ ...td, color:'#555', fontSize:'0.78rem' }}>{(inv.lines?.length || 1)} ligne(s)</td>
                        <td style={{ ...td, fontVariantNumeric:'tabular-nums', color:'#888' }}>${(inv.subtotal || inv.amount || 0).toLocaleString('fr-CA', {minimumFractionDigits:2})}</td>
                        <td style={td}>
                          {inv.taxable
                            ? <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:100, fontSize:'0.65rem', fontWeight:700, color:'var(--accent,#C8FF00)', background:'rgba(200,255,0,0.08)', border:'1px solid rgba(200,255,0,0.2)', whiteSpace:'nowrap' }}>TPS+TVQ</span>
                            : <span style={{ color:'#333' }}>—</span>}
                        </td>
                        <td style={{ ...td, fontWeight:800, color:'var(--accent,#C8FF00)', fontVariantNumeric:'tabular-nums' }}>${(inv.total || inv.amount || 0).toLocaleString('fr-CA', {minimumFractionDigits:2})}</td>
                        <td style={td}>{inv.dueDate || '—'}</td>
                        <td style={td}><Badge status={inv.status} map={INVOICE_STATUS}/></td>
                        <td style={td}>
                          <div style={{ display:'flex', gap:5 }}>
                            <IconBtn icon={I.print} onClick={()=>printInvoice(inv)}/>
                            <IconBtn icon={I.pen} onClick={()=>setIModal({...inv})}/>
                            <IconBtn icon={I.bin} onClick={()=>delInvoice(inv.id)} danger/>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {invoices.length === 0 && <div style={{ color:'#333', fontSize:'0.88rem', textAlign:'center', padding:'60px 0' }}>Aucune facture pour l'instant</div>}
            </div>
          </div>
        )}
      </main>

      {/* ══ CLIENT DETAIL PANEL ══ */}
      {selClient && sec===1 && (
        <div style={{ position:'fixed', inset:0, zIndex:200 }} onClick={()=>setSelClient(null)}>
          <div style={{ position:'absolute', right:0, top:0, bottom:0, width:360, background:'#0a0a0d', borderLeft:'1px solid rgba(255,255,255,0.06)', overflowY:'auto', padding:28, animation:'fadeIn 0.2s ease' }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <span style={{ fontSize:'0.65rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' }}>Profil client</span>
              <button style={{ background:'none', border:'none', color:'#444', cursor:'pointer', fontSize:'1.3rem' }} onClick={()=>setSelClient(null)}>×</button>
            </div>
            <div style={{ textAlign:'center', marginBottom:24 }}>
              <Avatar name={selClient.name} size={64}/>
              <div style={{ fontWeight:800, color:'#EAEAEA', fontSize:'1.1rem', marginTop:12, letterSpacing:'-0.02em' }}>{selClient.name}</div>
              <div style={{ color:'#444', fontSize:'0.8rem', marginBottom:10 }}>{selClient.company || selClient.email}</div>
              <Badge status={selClient.status}/>
            </div>
            {[['Courriel',selClient.email],['Téléphone',selClient.phone||'—'],['Entreprise',selClient.company||'—'],['Projet',selClient.project||'—'],['Budget',selClient.budget?`$${parseFloat(selClient.budget).toLocaleString()}`:'—']].map(([k,v])=>(
              <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ color:'#333', fontSize:'0.75rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>{k}</span>
                <span style={{ color:'#888', fontSize:'0.82rem', maxWidth:200, textAlign:'right', wordBreak:'break-all' }}>{v}</span>
              </div>
            ))}
            {selClient.notes && (
              <div style={{ marginTop:16, padding:14, background:'rgba(255,255,255,0.02)', borderRadius:8, border:'1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontSize:'0.65rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>Notes</div>
                <div style={{ color:'#666', fontSize:'0.82rem', lineHeight:1.65 }}>{selClient.notes}</div>
              </div>
            )}
            {(() => {
              const clientSites = websites.filter(w =>
                w.client && selClient.name &&
                w.client.toLowerCase().trim() === selClient.name.toLowerCase().trim()
              );
              if (clientSites.length === 0) return null;
              return (
                <div style={{ marginTop:20, padding:14, background:'rgba(139,92,246,0.06)', border:'1px solid rgba(139,92,246,0.2)', borderRadius:10 }}>
                  <div style={{ fontSize:'0.65rem', color:'#8B5CF6', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Sites associés</div>
                  {clientSites.map(w => (
                    <div key={w.id} style={{ marginBottom:10 }}>
                      <div style={{ fontSize:'0.82rem', fontWeight:700, color:'#EAEAEA', marginBottom:6 }}>{w.name}</div>
                      <div style={{ display:'flex', gap:6 }}>
                        {w.identifier && (
                          <a href={`http://localhost:3000/${w.identifier}`} target="_blank" rel="noreferrer"
                            style={{ ...btn.outline, textDecoration:'none', fontSize:'0.72rem', padding:'5px 10px' }}>
                            {I.globe} Voir
                          </a>
                        )}
                        {w.identifier && (
                          <button onClick={()=>{ setSelClient(null); openSiteEditor(w); }}
                            style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(200,255,0,0.08)', color:'#C8FF00', border:'1px solid rgba(200,255,0,0.25)', padding:'5px 10px', borderRadius:7, fontWeight:700, fontSize:'0.72rem', cursor:'pointer' }}>
                            ✎ Contenu
                          </button>
                        )}
                        {w.identifier && (
                          <button onClick={()=>{ setSelClient(null); setFtpModal({target:w.identifier,name:w.name}); setFtpLog([]); setFtpRunning(false); }}
                            style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(96,165,250,0.08)', color:'#60a5fa', border:'1px solid rgba(96,165,250,0.25)', padding:'5px 10px', borderRadius:7, fontWeight:700, fontSize:'0.72rem', cursor:'pointer' }}>
                            ↑ FTP
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:24 }}>
              <button style={btn.primary} onClick={()=>{setCModal({...selClient});setSelClient(null);}}>
                {I.pen} Modifier
              </button>
              <button style={{ ...btn.ghost, color:'#f87171', borderColor:'rgba(248,113,113,0.2)' }} onClick={()=>{setDelTarget({...selClient,_type:'client'});setSelClient(null);}}>
                {I.bin} Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ CLIENT MODAL ══ */}
      {cModal && (
        <Modal title={cModal.id ? 'Modifier le client' : 'Nouveau client'} onClose={()=>setCModal(null)} onSave={saveClient}>
          {cModal._error && (
            <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', marginBottom:16, color:'#f87171', fontSize:'0.82rem', fontWeight:600 }}>
              ⚠ {cModal._error}
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' }}>
            {[['Nom complet *','name','Jean Tremblay'],['Entreprise','company','ACME inc.'],['Courriel *','email','jean@exemple.com','email'],['Téléphone','phone','514-555-1234'],['Type de projet','project','Site web, logo...'],['Budget ($)','budget','2500','number']].map(([l,k,ph,t])=>(
              <Field key={k} label={l} value={{ value:cModal[k]||'', onChange:e=>setCModal({...cModal,[k]:e.target.value}), type:t||'text', placeholder:ph }}/>
            ))}
          </div>
          <Sel label="Statut" value={cModal.status||'prospect'} onChange={v=>setCModal({...cModal,status:v})}
            options={Object.entries(CLIENT_STAGES).map(([k,v])=>({v:k,l:v.label}))}/>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:'0.7rem', color:'#444', marginBottom:5, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>Notes</label>
            <textarea style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'10px 12px', color:'#EAEAEA', fontSize:'0.88rem', outline:'none', resize:'vertical', minHeight:72, boxSizing:'border-box' }}
              value={cModal.notes||''} onChange={e=>setCModal({...cModal,notes:e.target.value})} placeholder="Remarques internes..."/>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:'0.7rem', color:'#888', marginBottom:5, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>Clé API Anthropic (Claude) du client</label>
            <input style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:8, padding:'10px 12px', color:'#EAEAEA', fontSize:'0.85rem', outline:'none', boxSizing:'border-box', fontFamily:'monospace' }}
              type="password" value={cModal.anthropicApiKey||''} onChange={e=>setCModal({...cModal,anthropicApiKey:e.target.value})} placeholder="sk-ant-api03-... (optionnel)"/>
            <div style={{ fontSize:'0.7rem', color:'#333', marginTop:4 }}>Si remplie, les générations IA pour ce client sont facturées sur son compte Anthropic.</div>
          </div>
        </Modal>
      )}

      {/* ══ WEBSITE MODAL ══ */}
      {wModal && (
        <Modal title={wModal.id ? 'Modifier le site' : 'Nouveau site web'} onClose={()=>setWModal(null)} onSave={saveWebsite} saving={wSaving}>
          {wModal._error && (
            <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', marginBottom:16, color:'#f87171', fontSize:'0.82rem', fontWeight:600 }}>
              ⚠ {wModal._error}
            </div>
          )}
          {!wModal.id && (
            <div style={{ marginBottom:16 }}>
              {/* Identifiant + Type + Ville */}
              <div style={{ padding:'14px 16px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, marginBottom:14 }}>
                <Field label="Identifiant unique" value={{ value:wModal.identifier||'', onChange:e=>setWModal({...wModal,identifier:e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'')}), placeholder:'monrestaurant' }}/>
                <div style={{ marginBottom:12 }}>
                  <label style={{ display:'block', fontSize:'0.7rem', color:'#444', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Type de site</label>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:5 }}>
                    {[['vitrine','◈','Vitrine'],['restaurant','🍽️','Restaurant'],['coiffure','✂️','Coiffure'],['tech','⚡','Tech / App'],['commerce','🛍️','Commerce'],['sante','🏥','Santé']].map(([v,icon,label])=>(
                      <button key={v} onClick={()=>setWModal({...wModal,siteType:v})}
                        style={{ background:(wModal.siteType||'vitrine')===v?'rgba(200,255,0,0.1)':'rgba(255,255,255,0.02)', border:`1px solid ${(wModal.siteType||'vitrine')===v?'rgba(200,255,0,0.35)':'rgba(255,255,255,0.07)'}`, borderRadius:7, padding:'8px 5px', cursor:'pointer', color:(wModal.siteType||'vitrine')===v?'#C8FF00':'#555', fontSize:'0.72rem', fontWeight:700, display:'flex', flexDirection:'column', alignItems:'center', gap:3, transition:'all 0.15s' }}>
                        <span style={{fontSize:'1rem'}}>{icon}</span>{label}
                      </button>
                    ))}
                  </div>
                </div>
                <Field label="Ville / Région" value={{ value:wModal.location||'', onChange:e=>setWModal({...wModal,location:e.target.value}), placeholder:'Montréal, QC' }}/>
                <div style={{ fontSize:'0.7rem', color:'#444', marginTop:-6 }}>Dev : <span style={{color:'#C8FF00'}}>localhost:3000/{wModal.identifier||'monsite'}</span></div>
              </div>

              {/* ✨ PANNEAU IA */}
              <div style={{ background:'linear-gradient(135deg, rgba(200,255,0,0.04), rgba(100,200,255,0.04))', border:'1px solid rgba(200,255,0,0.15)', borderRadius:12, padding:'16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                  <span style={{ fontSize:'1rem' }}>✨</span>
                  <span style={{ fontWeight:800, fontSize:'0.85rem', color:'#C8FF00' }}>Générer avec Claude IA</span>
                  <span style={{ fontSize:'0.68rem', color:'#333', marginLeft:'auto' }}>claude-sonnet-4-6</span>
                </div>

                <div style={{ marginBottom:10 }}>
                  <label style={{ display:'block', fontSize:'0.7rem', color:'#444', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:5 }}>Décris le client</label>
                  <textarea
                    value={wModal.aiDescription||''}
                    onChange={e=>setWModal({...wModal,aiDescription:e.target.value})}
                    placeholder="Ex: Restaurant italien familial fondé en 1998, spécialité pâtes fraîches et pizzas au feu de bois, clientèle locale, ambiance chaleureuse, prix moyens..."
                    style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'10px 12px', color:'#EAEAEA', fontSize:'0.85rem', outline:'none', resize:'vertical', minHeight:80, boxSizing:'border-box' }}/>
                </div>

                {/* Photo d'inspiration */}
                <div style={{ marginBottom:10 }}>
                  <label style={{ display:'block', fontSize:'0.7rem', color:'#444', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Photo d'inspiration (optionnel)</label>
                  <div
                    onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor='#C8FF00';}}
                    onDragLeave={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,0.08)';}}
                    onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor='rgba(255,255,255,0.08)';handleInspirationFile(e.dataTransfer.files[0]);}}
                    style={{ border:'1px dashed rgba(255,255,255,0.08)', borderRadius:8, padding:'12px', textAlign:'center', transition:'border-color 0.2s', cursor:'pointer', position:'relative' }}
                    onClick={()=>document.getElementById('inspFile').click()}>
                    <input id="inspFile" type="file" accept="image/*" style={{ display:'none' }} onChange={e=>handleInspirationFile(e.target.files[0])}/>
                    {wModal.inspirationB64
                      ? <div style={{ display:'flex', alignItems:'center', gap:8, justifyContent:'center' }}>
                          <img src={wModal.inspirationB64} alt="" style={{ height:40, width:60, objectFit:'cover', borderRadius:5 }}/>
                          <span style={{ fontSize:'0.75rem', color:'#C8FF00', fontWeight:700 }}>{wModal.inspirationName}</span>
                          <button onClick={e=>{e.stopPropagation();setWModal(m=>({...m,inspirationB64:null,inspirationUrl:null,inspirationName:null}));}} style={{ background:'none', border:'none', color:'#444', cursor:'pointer', fontSize:'1rem' }}>×</button>
                        </div>
                      : <div style={{ fontSize:'0.75rem', color:'#444' }}>Glisse une photo ici ou clique · PNG, JPG, WebP</div>
                    }
                  </div>
                  <div style={{ marginTop:6 }}>
                    <input value={wModal.inspirationUrl||''} onChange={e=>setWModal(m=>({...m,inspirationUrl:e.target.value,inspirationB64:null,inspirationName:null}))}
                      placeholder="Ou colle une URL d'image publique..."
                      style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:7, padding:'8px 11px', color:'#EAEAEA', fontSize:'0.8rem', outline:'none', boxSizing:'border-box' }}/>
                  </div>
                  <div style={{ fontSize:'0.68rem', color:'#333', marginTop:4 }}>Claude analyse les couleurs et l'ambiance de cette image pour le design</div>
                </div>

                {/* Photo hero du site */}
                <Field label="URL photo hero du site (optionnel)" value={{ value:wModal.customPhotoUrl||'', onChange:e=>setWModal({...wModal,customPhotoUrl:e.target.value}), placeholder:'https://images.unsplash.com/photo-...' }}/>

                <button onClick={generateWithAI} disabled={aiLoading||!wModal.name||!wModal.aiDescription}
                  style={{ width:'100%', background: aiLoading||!wModal.name||!wModal.aiDescription ? 'rgba(200,255,0,0.2)' : 'linear-gradient(135deg,#C8FF00,#80ff60)', color:'#000', border:'none', padding:'11px', borderRadius:8, fontWeight:800, fontSize:'0.85rem', cursor: aiLoading||!wModal.name||!wModal.aiDescription ? 'not-allowed':'pointer', transition:'opacity 0.2s', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  {aiLoading ? <><span style={{ display:'inline-block', width:14, height:14, border:'2px solid #000', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/> Claude génère...</> : '✨ Générer le design complet'}
                </button>

                {aiPreview && (
                  <div style={{ marginTop:12, padding:'12px', background:'rgba(200,255,0,0.06)', border:'1px solid rgba(200,255,0,0.2)', borderRadius:8 }}>
                    <div style={{ fontSize:'0.72rem', color:'#C8FF00', fontWeight:700, marginBottom:8 }}>✅ Design généré — aperçu :</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, fontSize:'0.75rem' }}>
                      <div style={{ color:'#555' }}>Accent</div>
                      <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                        <span style={{ width:12, height:12, borderRadius:'50%', background:`linear-gradient(135deg,${aiPreview.accentFrom},${aiPreview.accentTo})`, display:'inline-block' }}/>
                        <span style={{ color:'#888' }}>{aiPreview.accentFrom} → {aiPreview.accentTo}</span>
                      </div>
                      <div style={{ color:'#555' }}>Titre hero</div>
                      <div style={{ color:'#aaa', fontSize:'0.7rem' }}>{aiPreview.hero?.titleLine1} {aiPreview.hero?.titleAccent}</div>
                      <div style={{ color:'#555' }}>Services</div>
                      <div style={{ color:'#aaa' }}>{aiPreview.services?.length || 0} services</div>
                      <div style={{ color:'#555' }}>Photo</div>
                      <div style={{ color: aiPreview.heroImage ? '#C8FF00' : '#444' }}>{aiPreview.heroImage ? '✓ Incluse' : 'Aucune'}</div>
                    </div>
                    <div style={{ fontSize:'0.68rem', color:'#444', marginTop:8 }}>Ce design sera appliqué automatiquement au site créé.</div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Logo URL */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:'0.7rem', color:'#444', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Logo du site (URL)</label>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {wModal.logoUrl && (
                <img src={wModal.logoUrl} alt="logo" style={{ height:36, width:56, objectFit:'contain', borderRadius:6, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', padding:4, flexShrink:0 }}
                  onError={e=>e.target.style.display='none'}/>
              )}
              <input value={wModal.logoUrl||''} onChange={e=>setWModal({...wModal,logoUrl:e.target.value})}
                placeholder="https://firebasestorage.googleapis.com/..."
                style={{ flex:1, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'9px 12px', color:'#EAEAEA', fontSize:'0.85rem', outline:'none', boxSizing:'border-box' }}
                onFocus={e=>e.target.style.borderColor='rgba(200,255,0,0.4)'}
                onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.08)'}/>
            </div>
            <div style={{ fontSize:'0.68rem', color:'#333', marginTop:4 }}>🎯 Utilisé comme logo ET favicon automatiquement</div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' }}>
            {[['Nom du site *','name','Restaurant Le Balisier'],['Client','client','Jean Tremblay'],['URL','url','lebalisier.ca'],['Tech stack','techStack','React, WordPress...'],['Budget ($)','budget','4500','number'],['Échéance','dueDate','','date']].map(([l,k,ph,t])=>(
              <Field key={k} label={l} value={{ value:wModal[k]||'', onChange:e=>setWModal({...wModal,[k]:e.target.value}), type:t||'text', placeholder:ph }}/>
            ))}
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:'0.7rem', color:'#444', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', display:'block', marginBottom:5 }}>Avancement — {wModal.progress||0}%</label>
            <input type="range" min="0" max="100" value={wModal.progress||0} onChange={e=>setWModal({...wModal,progress:parseInt(e.target.value)})} style={{ width:'100%' }}/>
          </div>
          <Sel label="Statut" value={wModal.status||'en_dev'} onChange={v=>setWModal({...wModal,status:v})}
            options={Object.entries(WEB_STATUS).map(([k,v])=>({v:k,l:v.label}))}/>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:'0.7rem', color:'#444', marginBottom:5, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>Notes</label>
            <textarea style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'10px 12px', color:'#EAEAEA', fontSize:'0.88rem', outline:'none', resize:'vertical', minHeight:72, boxSizing:'border-box' }}
              value={wModal.notes||''} onChange={e=>setWModal({...wModal,notes:e.target.value})} placeholder="Notes de développement..."/>
          </div>
        </Modal>
      )}

      {/* ══ PROJECT MODAL ══ */}
      {pModal && (
        <Modal title={pModal.id ? 'Modifier le projet' : 'Nouveau projet'} onClose={()=>setPModal(null)} onSave={saveProject}>
          <Field label="Nom du projet *" value={{ value:pModal.name||'', onChange:e=>setPModal({...pModal,name:e.target.value}), placeholder:'Refonte site vitrine' }}/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' }}>
            <Field label="Client" value={{ value:pModal.client||'', onChange:e=>setPModal({...pModal,client:e.target.value}), placeholder:'Nom du client' }}/>
            <Field label="Échéance" value={{ value:pModal.dueDate||'', onChange:e=>setPModal({...pModal,dueDate:e.target.value}), type:'date' }}/>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:'0.7rem', color:'#444', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', display:'block', marginBottom:5 }}>Avancement — {pModal.progress||0}%</label>
            <input type="range" min="0" max="100" value={pModal.progress||0} onChange={e=>setPModal({...pModal,progress:parseInt(e.target.value)})} style={{ width:'100%' }}/>
          </div>
          <Sel label="Statut" value={pModal.status||'active'} onChange={v=>setPModal({...pModal,status:v})}
            options={Object.entries(CLIENT_STAGES).map(([k,v])=>({v:k,l:v.label}))}/>
        </Modal>
      )}

      {/* ══ INVOICE MODAL ══ */}
      {iModal && (
        <InvoiceModal
          iModal={iModal}
          setIModal={setIModal}
          clients={clients}
          availableItems={invoiceItems}
          onSave={saveInvoice}
          onClose={()=>setIModal(null)}
        />
      )}

      {/* ══ NOUVEAU SITE — TOAST COMPILATION ══ */}
      {newSiteReady && (
        <div style={{ position:'fixed', bottom:32, right:32, zIndex:9999, background:'#111', border:'1px solid rgba(200,255,0,0.3)', borderRadius:14, padding:'20px 24px', minWidth:300, boxShadow:'0 8px 40px rgba(0,0,0,0.6)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--accent,#C8FF00)', boxShadow:'0 0 8px var(--accent,#C8FF00)', flexShrink:0 }}/>
            <div style={{ fontWeight:800, fontSize:'0.9rem', color:'#EAEAEA' }}>Site "{newSiteReady.name}" créé !</div>
          </div>
          {newSiteReady.countdown > 0 ? (
            <div style={{ fontSize:'0.8rem', color:'#555' }}>
              Compilation webpack… <span style={{ color:'var(--accent,#C8FF00)', fontWeight:700 }}>{newSiteReady.countdown}s</span>
            </div>
          ) : (
            <div>
              <div style={{ fontSize:'0.78rem', color:'#666', marginBottom:12 }}>Prêt ! Ouvrez votre site :</div>
              <a href={`http://localhost:3000/${newSiteReady.target}`} target="_blank" rel="noreferrer"
                style={{ display:'block', background:'var(--accent,#C8FF00)', color:'#000', fontWeight:800, fontSize:'0.82rem', padding:'10px 16px', borderRadius:8, textAlign:'center', textDecoration:'none' }}>
                Ouvrir localhost:3000/{newSiteReady.target}
              </a>
              <button onClick={()=>setNewSiteReady(null)} style={{ width:'100%', marginTop:8, background:'none', border:'none', color:'#333', fontSize:'0.75rem', cursor:'pointer' }}>Fermer</button>
            </div>
          )}
        </div>
      )}

      {/* ══ CONFIRM DELETE ══ */}
      {delTarget && (
        <Confirm name={delTarget.name} onClose={()=>setDelTarget(null)}
          onConfirm={delTarget._type==='website' ? delWebsite : delClient}/>
      )}

      {/* ══ SITE CONTENT EDITOR ══ */}
      {siteEditor && (() => {
        const cfg = siteEditor.config;
        const tabs = [
          { id:'contact', label:'Coordonnées' },
          { id:'hero',    label:'Hero' },
          { id:'services',label:'Services' },
          { id:'about',   label:'À propos' },
          { id:'colors',  label:'Couleurs' },
          { id:'social',  label:'Réseaux' },
        ];
        const inp = (val, onChange, ph='', type='text') => (
          <input type={type} value={val||''} onChange={e=>onChange(e.target.value)} placeholder={ph}
            style={{ width:'100%', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'9px 12px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none', boxSizing:'border-box', marginBottom:12 }}
            onFocus={e=>e.target.style.borderColor='rgba(200,255,0,0.4)'}
            onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
        );
        const lbl = (t) => <div style={{ fontSize:'0.65rem', color:'#444', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>{t}</div>;
        const ta = (val, onChange, ph='', rows=3) => (
          <textarea value={val||''} onChange={e=>onChange(e.target.value)} placeholder={ph} rows={rows}
            style={{ width:'100%', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'9px 12px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none', resize:'vertical', boxSizing:'border-box', marginBottom:12 }}
            onFocus={e=>e.target.style.borderColor='rgba(200,255,0,0.4)'}
            onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
        );
        return (
          <div style={{ position:'fixed', inset:0, background:'#060608', zIndex:800, display:'flex', flexDirection:'column' }}>
            {/* Header */}
            <div style={{ display:'flex', alignItems:'center', gap:16, padding:'16px 28px', borderBottom:'1px solid rgba(255,255,255,0.07)', flexShrink:0 }}>
              <button onClick={()=>setSiteEditor(null)} style={{ background:'none', border:'none', color:'#555', cursor:'pointer', fontSize:'1.4rem', lineHeight:1, padding:4 }}>←</button>
              <div>
                <div style={{ fontSize:'0.65rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' }}>Éditeur de contenu</div>
                <div style={{ fontWeight:800, color:'#EAEAEA', fontSize:'1.05rem', letterSpacing:'-0.02em' }}>{siteEditor.name}</div>
              </div>
              <div style={{ marginLeft:'auto', display:'flex', gap:10 }}>
                <button onClick={()=>setSiteEditor(null)} style={{ ...btn.ghost }}>Annuler</button>
                <button onClick={saveSiteConfig} disabled={siteEditorSaving}
                  style={{ ...btn.primary, opacity: siteEditorSaving ? 0.6 : 1 }}>
                  {siteEditorSaving ? 'Sauvegarde...' : '✓ Sauvegarder'}
                </button>
              </div>
            </div>

            <div style={{ display:'flex', flex:1, overflow:'hidden' }}>
              {/* Left tabs */}
              <div style={{ width:160, borderRight:'1px solid rgba(255,255,255,0.06)', padding:'20px 0', flexShrink:0 }}>
                {tabs.map(t => (
                  <button key={t.id} onClick={()=>setSiteEditorTab(t.id)}
                    style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 20px', background: siteEditorTab===t.id ? 'rgba(200,255,0,0.08)' : 'transparent', borderLeft: siteEditorTab===t.id ? '2px solid #C8FF00' : '2px solid transparent', border:'none', borderRadius:0, color: siteEditorTab===t.id ? '#C8FF00' : '#555', fontSize:'0.82rem', fontWeight:700, cursor:'pointer', transition:'all 0.15s', letterSpacing:'0.02em' }}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Right content */}
              <div style={{ flex:1, overflowY:'auto', padding:'28px 36px', maxWidth:680 }}>

                {siteEditorTab === 'contact' && <>
                  {lbl('Nom du site')} {inp(cfg.name, v=>setEC({name:v}), 'Mon site web')}
                  {lbl('Téléphone')} {inp(cfg.phone, v=>setEC({phone:v}), '514-555-1234')}
                  {lbl('Courriel')} {inp(cfg.email, v=>setEC({email:v}), 'contact@monsite.ca', 'email')}
                  {lbl('Adresse')} {inp(cfg.address, v=>setEC({address:v}), 'Montréal, QC')}
                  {lbl('Slogan (tagline)')} {inp(cfg.tagline, v=>setEC({tagline:v}), 'Votre tagline ici')}
                  {lbl('CTA principal (bouton)')} {inp(cfg.ctaLabel, v=>setEC({ctaLabel:v}), 'Nous contacter')}
                </>}

                {siteEditorTab === 'hero' && <>
                  {lbl('Image hero (URL)')} {inp(cfg.heroImage||'', v=>setEC({heroImage:v}), 'https://images.unsplash.com/photo-...')}
                  {cfg.heroImage && <img src={cfg.heroImage} alt="" style={{ width:'100%', maxHeight:160, objectFit:'cover', borderRadius:10, marginBottom:16, border:'1px solid rgba(255,255,255,0.08)' }} onError={e=>e.target.style.display='none'}/>}
                  {lbl('Ligne 1')} {inp(cfg.hero?.titleLine1, v=>setEHero({titleLine1:v}), 'DES SITES')}
                  {lbl('Ligne 1 accent')} {inp(cfg.hero?.titleAccent, v=>setEHero({titleAccent:v}), 'QUI VENDENT')}
                  {lbl('Ligne 2')} {inp(cfg.hero?.titleLine2, v=>setEHero({titleLine2:v}), 'ET DES APPS')}
                  {lbl('Ligne 2 accent')} {inp(cfg.hero?.titleAccent2, v=>setEHero({titleAccent2:v}), 'QUI PERFORMENT')}
                  {lbl('Sous-titre')} {ta(cfg.hero?.subtitle, v=>setEHero({subtitle:v}), 'Description courte...')}
                  {lbl('Bouton CTA hero')} {inp(cfg.hero?.cta, v=>setEHero({cta:v}), 'Nous contacter →')}
                  {lbl('Bouton secondaire')} {inp(cfg.hero?.ctaSecond, v=>setEHero({ctaSecond:v}), 'En savoir plus')}
                </>}

                {siteEditorTab === 'services' && (cfg.services||[]).map((s,i) => (
                  <div key={i} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'16px', marginBottom:14 }}>
                    <div style={{ fontSize:'0.75rem', color:'#555', fontWeight:700, marginBottom:10 }}>Service {i+1}</div>
                    {lbl('Icône')} {inp(s.icon, v=>setEService(i,{icon:v}), '◈')}
                    {lbl('Titre')} {inp(s.title, v=>setEService(i,{title:v}), 'Nom du service')}
                    {lbl('Description')} {ta(s.desc, v=>setEService(i,{desc:v}), 'Description...', 2)}
                  </div>
                ))}

                {siteEditorTab === 'about' && <>
                  {lbl('Titre de la section')} {inp(cfg.about?.title, v=>setEAbout({title:v}), 'À propos')}
                  {lbl('Texte')} {ta(cfg.about?.text, v=>setEAbout({text:v}), 'Parlez de vous...', 5)}
                  <div style={{ marginTop:8 }}>
                    <div style={{ fontSize:'0.65rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>Témoignages</div>
                    {(cfg.testimonials||[]).map((t,i) => (
                      <div key={i} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'14px', marginBottom:12 }}>
                        <div style={{ fontSize:'0.72rem', color:'#444', fontWeight:700, marginBottom:8 }}>Témoignage {i+1}</div>
                        {lbl('Nom')} {inp(t.name, v=>setETestimonial(i,{name:v}), 'Marie Tremblay')}
                        {lbl('Rôle / Source')} {inp(t.role, v=>setETestimonial(i,{role:v}), 'Client vérifié')}
                        {lbl('Témoignage')} {ta(t.text, v=>setETestimonial(i,{text:v}), 'Super service...', 2)}
                      </div>
                    ))}
                  </div>
                </>}

                {siteEditorTab === 'colors' && <>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
                    <div>
                      {lbl('Couleur accent début')}
                      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                        <input type="color" value={cfg.accentFrom||'#C8FF00'} onChange={e=>setEC({accentFrom:e.target.value})}
                          style={{ width:40, height:36, borderRadius:8, border:'1px solid rgba(255,255,255,0.1)', background:'none', cursor:'pointer', padding:2 }}/>
                        {inp(cfg.accentFrom, v=>setEC({accentFrom:v}), '#C8FF00')}
                      </div>
                    </div>
                    <div>
                      {lbl('Couleur accent fin')}
                      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                        <input type="color" value={cfg.accentTo||'#80ff60'} onChange={e=>setEC({accentTo:e.target.value})}
                          style={{ width:40, height:36, borderRadius:8, border:'1px solid rgba(255,255,255,0.1)', background:'none', cursor:'pointer', padding:2 }}/>
                        {inp(cfg.accentTo, v=>setEC({accentTo:v}), '#80ff60')}
                      </div>
                    </div>
                  </div>
                  <div style={{ height:60, borderRadius:12, background:`linear-gradient(135deg, ${cfg.accentFrom||'#C8FF00'}, ${cfg.accentTo||'#80ff60'})`, marginBottom:20, border:'1px solid rgba(255,255,255,0.06)' }}/>
                  {lbl('Fond principal')}
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <input type="color" value={cfg.darkBg||'#080808'} onChange={e=>setEC({darkBg:e.target.value})}
                      style={{ width:40, height:36, borderRadius:8, border:'1px solid rgba(255,255,255,0.1)', background:'none', cursor:'pointer', padding:2 }}/>
                    {inp(cfg.darkBg, v=>setEC({darkBg:v}), '#080808')}
                  </div>
                  <div style={{ marginTop:12, padding:'12px 16px', background:cfg.darkBg||'#080808', borderRadius:10, border:'1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ fontWeight:800, background:`linear-gradient(135deg,${cfg.accentFrom||'#C8FF00'},${cfg.accentTo||'#80ff60'})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Aperçu des couleurs</span>
                    <span style={{ color:'#EAEAEA', marginLeft:12, fontSize:'0.85rem' }}>Texte normal</span>
                  </div>
                </>}

                {siteEditorTab === 'social' && <>
                  {lbl('Facebook (URL ou @)')} {inp(cfg.social?.facebook, v=>setESocial({facebook:v}), 'https://facebook.com/...')}
                  {lbl('Instagram (URL ou @)')} {inp(cfg.social?.instagram, v=>setESocial({instagram:v}), '@moncompte')}
                  {lbl('LinkedIn (URL)')} {inp(cfg.social?.linkedin, v=>setESocial({linkedin:v}), 'https://linkedin.com/in/...')}
                </>}

              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ FIREBASE DEPLOY MODAL ══ */}
      {fbModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', backdropFilter:'blur(8px)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }} onClick={()=>{ if(!fbRunning) setFbModal(null); }}>
          <div style={{ background:'#0e0e12', border:'1px solid rgba(255,160,0,0.2)', borderRadius:16, padding:32, width:'100%', maxWidth:520 }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div>
                <div style={{ fontSize:'0.65rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' }}>Déploiement</div>
                <h3 style={{ fontWeight:800, color:'#EAEAEA', fontSize:'1.05rem', letterSpacing:'-0.02em', marginTop:2 }}>🔥 Firebase — {fbModal.name}</h3>
              </div>
              {!fbRunning && <button onClick={()=>setFbModal(null)} style={{ background:'none', border:'none', color:'#444', cursor:'pointer', fontSize:'1.4rem' }}>×</button>}
            </div>

            <div style={{ padding:'12px 14px', background:'rgba(255,160,0,0.06)', border:'1px solid rgba(255,160,0,0.15)', borderRadius:8, marginBottom:16, fontSize:'0.78rem', color:'#888' }}>
              Le site sera buildé (~2-3 min) puis déployé automatiquement sur :<br/>
              <span style={{ color:'#FFAB00', fontWeight:700 }}>https://ucreatech-{fbModal.target}.web.app</span>
            </div>

            {fbLog.length > 0 && (
              <div style={{ background:'#060608', border:'1px solid rgba(255,255,255,0.06)', borderRadius:10, padding:'12px 14px', maxHeight:220, overflowY:'auto', marginBottom:16, fontFamily:'monospace', fontSize:'0.73rem' }}>
                {fbLog.map((l,i) => (
                  <div key={i} style={{ marginBottom:3, color: l.startsWith('✅')||l.startsWith('🎉') ? '#4ade80' : l.startsWith('❌') ? '#f87171' : l.startsWith('🔨')||l.startsWith('📡') ? '#FFAB00' : '#555' }}>{l}</div>
                ))}
              </div>
            )}

            {fbDoneUrl && (
              <a href={fbDoneUrl} target="_blank" rel="noreferrer"
                style={{ display:'block', background:'linear-gradient(135deg,#FFAB00,#FF6D00)', color:'#000', fontWeight:800, fontSize:'0.85rem', padding:'12px', borderRadius:8, textAlign:'center', textDecoration:'none', marginBottom:12 }}>
                🌐 Ouvrir {fbDoneUrl.replace('https://','')}
              </a>
            )}

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              {!fbRunning && <button onClick={()=>setFbModal(null)} style={btn.ghost}>Fermer</button>}
              {!fbDoneUrl && (
                <button onClick={startFirebaseDeploy} disabled={fbRunning}
                  style={{ ...btn.primary, background: fbRunning ? 'rgba(255,160,0,0.2)' : '#FFAB00', color: fbRunning ? '#FFAB00' : '#000', cursor: fbRunning ? 'not-allowed' : 'pointer' }}>
                  {fbRunning
                    ? <><span style={{ display:'inline-block', width:12, height:12, border:'2px solid currentColor', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/> Déploiement...</>
                    : '🔥 Lancer le déploiement'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ FTP DEPLOY MODAL ══ */}
      {ftpModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', backdropFilter:'blur(8px)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }} onClick={()=>{ if(!ftpRunning) setFtpModal(null); }}>
          <div style={{ background:'#0e0e12', border:'1px solid rgba(96,165,250,0.2)', borderRadius:16, padding:32, width:'100%', maxWidth:520 }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <div>
                <div style={{ fontSize:'0.65rem', color:'#333', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' }}>Déploiement</div>
                <h3 style={{ fontWeight:800, color:'#EAEAEA', fontSize:'1.05rem', letterSpacing:'-0.02em', marginTop:2 }}>FTP — {ftpModal.name}</h3>
              </div>
              {!ftpRunning && <button onClick={()=>setFtpModal(null)} style={{ background:'none', border:'none', color:'#444', cursor:'pointer', fontSize:'1.4rem' }}>×</button>}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 14px' }}>
              {[['Serveur FTP','host','ftp.monsite.ca'],['Utilisateur','user','ftp_user'],['Mot de passe','password','●●●●●●●●'],['Dossier distant','remoteDir','/public_html']].map(([l,k,ph])=>(
                <div key={k} style={{ marginBottom:12 }}>
                  <label style={{ display:'block', fontSize:'0.65rem', color:'#444', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>{l}</label>
                  <input type={k==='password'?'password':'text'} value={ftpForm[k]} onChange={e=>setFtpForm(f=>({...f,[k]:e.target.value}))} placeholder={ph} disabled={ftpRunning}
                    style={{ width:'100%', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'9px 12px', color:'#EAEAEA', fontSize:'0.85rem', outline:'none', boxSizing:'border-box' }}/>
                </div>
              ))}
            </div>

            <div style={{ fontSize:'0.7rem', color:'#444', marginBottom:16, padding:'8px 12px', background:'rgba(96,165,250,0.06)', borderRadius:8, border:'1px solid rgba(96,165,250,0.1)' }}>
              Le build React sera généré (~2-3 min) puis uploadé via FTP dans le dossier indiqué.
            </div>

            {ftpLog.length > 0 && (
              <div style={{ background:'#060608', border:'1px solid rgba(255,255,255,0.06)', borderRadius:10, padding:'12px 14px', maxHeight:200, overflowY:'auto', marginBottom:16, fontFamily:'monospace', fontSize:'0.75rem', color:'#555' }}>
                {ftpLog.map((l,i) => <div key={i} style={{ marginBottom:3, color: l.startsWith('✅')||l.startsWith('🎉') ? '#4ade80' : l.startsWith('❌') ? '#f87171' : '#888' }}>{l}</div>)}
              </div>
            )}

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              {!ftpRunning && <button onClick={()=>setFtpModal(null)} style={btn.ghost}>Fermer</button>}
              <button onClick={startFtpDeploy} disabled={ftpRunning}
                style={{ ...btn.primary, background: ftpRunning ? 'rgba(96,165,250,0.2)' : '#60a5fa', color: ftpRunning ? '#60a5fa' : '#000', cursor: ftpRunning ? 'not-allowed' : 'pointer' }}>
                {ftpRunning ? <><span style={{ display:'inline-block', width:12, height:12, border:'2px solid currentColor', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/> Déploiement...</> : '↑ Lancer le déploiement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ PROSPECTION ══ */}
      {sec === 7  && <ErrorBoundary label="Prospection"><ProspectionSection /></ErrorBoundary>}
      {sec === 8  && <ErrorBoundary label="SEO"><SEOSection /></ErrorBoundary>}
      {sec === 9  && <ErrorBoundary label="Analytics"><AnalyticsSection /></ErrorBoundary>}
      {sec === 16 && <ErrorBoundary label="Fiche Google"><GmbSection /></ErrorBoundary>}
      {sec === 18 && <ErrorBoundary label="Prospection Libre"><ProspectionFreeSection /></ErrorBoundary>}

      {/* ══ PRÉFÉRENCES ══ */}
      {sec === 10 && <ErrorBoundary label="Préférences"><PreferencesSection /></ErrorBoundary>}

      {/* ══ ACCÈS UTILISATEURS ══ */}
      {sec === 11 && <ErrorBoundary label="Accès"><AccessSection currentUserUid={currentUser?.uid} /></ErrorBoundary>}

      {/* ══ LÉGAL IA ══ */}
      {sec === 12 && <ErrorBoundary label="Légal IA"><LegalGeneratorSection /></ErrorBoundary>}

      {/* ══ AUDITS SEO ══ */}
      {sec === 13 && <ErrorBoundary label="Audits SEO"><AuditsSection audits={audits} competitorReports={competitorReports} gmbReports={gmbReports} /></ErrorBoundary>}
      {sec === 14 && <ErrorBoundary label="Mail Marketing"><MarketingSection /></ErrorBoundary>}
      {sec === 15 && <ErrorBoundary label="Tâches"><TasksSection /></ErrorBoundary>}
      {sec === 17 && <ErrorBoundary label="Items"><ItemsSection /></ErrorBoundary>}

      {/* ══ OUTILS GRATUITS ══ */}
      {sec === 19 && <ErrorBoundary label="Devis"><QuoteCalculatorSection /></ErrorBoundary>}
      {sec === 20 && <ErrorBoundary label="Domaine"><DomainCheckerSection /></ErrorBoundary>}
      {sec === 21 && <ErrorBoundary label="QR Code"><QRGeneratorSection /></ErrorBoundary>}
      {sec === 22 && <ErrorBoundary label="Palette"><BrandPaletteSection /></ErrorBoundary>}
      {sec === 23 && <ErrorBoundary label="Thème"><ThemeSection /></ErrorBoundary>}
      {sec === 24 && <ErrorBoundary label="Vybme Admin"><VibeeAdminSection /></ErrorBoundary>}
      {sec === 25 && <ErrorBoundary label="Graph View"><GraphViewSection websites={websites} clients={clients} /></ErrorBoundary>}
      {sec === 27 && <ErrorBoundary label="Vybme Revenus"><VibeeRevenueSection /></ErrorBoundary>}
      {sec === 28 && <ErrorBoundary label="Vybme Visiteurs"><VibeeStatsSection /></ErrorBoundary>}
      {sec === 29 && <ErrorBoundary label="QS Usagers"><QSUsersSection /></ErrorBoundary>}

      {/* ══ CONTRATS IA ══ */}
      {sec === 6 && (
        <ContractsSection
          contracts={contracts} ctModal={ctModal} setCtModal={setCtModal}
          ctForm={ctForm} setCtForm={setCtForm}
          ctGenerated={ctGenerated} setCtGenerated={setCtGenerated}
          ctGenerating={ctGenerating} setCtGenerating={setCtGenerating}
          ctSaving={ctSaving} setCtSaving={setCtSaving}
          ctDelTarget={ctDelTarget} setCtDelTarget={setCtDelTarget}
          ctViewing={ctViewing} setCtViewing={setCtViewing}
          ax={ax} firestore={firestore}
        />
      )}

      {/* ══ MOBILE BOTTOM NAV ══ */}
      {isMobile && (
        <nav style={{ position:'fixed', bottom:0, left:0, right:0, background:'#0a0a0d', borderTop:'1px solid rgba(255,255,255,0.07)', display:'flex', zIndex:100, paddingBottom:'env(safe-area-inset-bottom)' }}>
          {NAV.map((item) => (
            <button key={item.sec} onClick={()=>setSec(item.sec)} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4, padding:'10px 4px 8px', border:'none', background:'transparent', color: sec===item.sec ? 'var(--accent,#C8FF00)' : '#444', cursor:'pointer', transition:'color 0.15s', position:'relative' }}>
              {item.badge > 0 && <span style={{ position:'absolute', top:6, right:'50%', transform:'translateX(10px)', fontSize:'0.55rem', fontWeight:900, background:'var(--accent,#C8FF00)', color:'#000', borderRadius:99, minWidth:14, height:14, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px' }}>{item.badge}</span>}
              <span style={{ opacity: sec===item.sec ? 1 : 0.5 }}>{item.icon}</span>
              <span style={{ fontSize:'0.58rem', fontWeight:700, letterSpacing:'0.02em' }}>{item.label}</span>
            </button>
          ))}
        </nav>
      )}

      {/* ══ AI SITE FACTORY MODAL ══ */}
      {aiFactory && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', backdropFilter:'blur(6px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
          onClick={e => { if (e.target === e.currentTarget && !aiFactoryLoading) { setAiFactory(null); setAiFactoryResult(null); } }}>
          <div style={{ background:'#0e0e12', border:'1px solid rgba(200,255,0,0.2)', borderRadius:18, width:'100%', maxWidth:520, padding:32, position:'relative', boxShadow:'0 0 80px rgba(200,255,0,0.08)' }}>

            <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg, transparent, #C8FF00, transparent)', borderRadius:'18px 18px 0 0' }}/>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
              <div>
                <div style={{ fontSize:'0.65rem', color:'#C8FF00', fontWeight:800, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:4 }}>✦ IA</div>
                <h2 style={{ fontSize:'1.4rem', fontWeight:900, color:'#EAEAEA', letterSpacing:'-0.03em', margin:0 }}>AI Site Factory</h2>
              </div>
              {!aiFactoryLoading && (
                <button onClick={() => { setAiFactory(null); setAiFactoryResult(null); }}
                  style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, color:'#555', cursor:'pointer', width:32, height:32, fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  ✕
                </button>
              )}
            </div>

            {aiFactoryLoading && (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:20, padding:'20px 0' }}>
                <style>{`@keyframes factoryPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.6;transform:scale(0.92)}}`}</style>
                <div style={{ width:56, height:56, borderRadius:'50%', border:'2px solid rgba(200,255,0,0.15)', borderTop:'2px solid #C8FF00', animation:'spin 0.8s linear infinite' }}/>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:'1rem', fontWeight:700, color:'#EAEAEA', marginBottom:6 }}>Génération en cours…</div>
                  <div style={{ fontSize:'0.8rem', color:'#555' }}>Claude analyse et crée le site complet</div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8, width:'100%' }}>
                  {['Génération du contenu IA', 'Création du dossier source', 'Enregistrement Firestore'].map((step, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px', background:'rgba(200,255,0,0.04)', border:'1px solid rgba(200,255,0,0.1)', borderRadius:9 }}>
                      <div style={{ width:6, height:6, borderRadius:'50%', background:'#C8FF00', animation:'factoryPulse 1.4s ease-in-out infinite', animationDelay:`${i*0.4}s` }}/>
                      <span style={{ fontSize:'0.82rem', color:'#888' }}>{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!aiFactoryLoading && aiFactoryResult && (
              <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:40, height:40, borderRadius:'50%', background:'rgba(74,222,128,0.12)', border:'1px solid rgba(74,222,128,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', color:'#4ade80' }}>✓</div>
                  <div>
                    <div style={{ fontWeight:800, color:'#4ade80', fontSize:'1rem' }}>Site créé !</div>
                    <div style={{ fontSize:'0.78rem', color:'#555' }}>{aiFactoryResult.name}</div>
                  </div>
                </div>

                <div style={{ borderRadius:10, overflow:'hidden', height:48, background:`linear-gradient(135deg, ${aiFactoryResult.config?.accentFrom}, ${aiFactoryResult.config?.accentTo})`, border:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 16px' }}>
                  <span style={{ fontSize:'0.72rem', fontWeight:700, color:'rgba(0,0,0,0.55)', fontFamily:'monospace' }}>{aiFactoryResult.config?.accentFrom}</span>
                  <span style={{ fontSize:'0.72rem', fontWeight:700, color:'rgba(0,0,0,0.55)', fontFamily:'monospace' }}>{aiFactoryResult.config?.accentTo}</span>
                </div>
                <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'12px 16px', display:'flex', gap:14, alignItems:'center' }}>
                  <div style={{ width:28, height:28, borderRadius:6, background: aiFactoryResult.config?.darkBg, border:'1px solid rgba(255,255,255,0.12)', flexShrink:0 }}/>
                  <div>
                    <div style={{ fontSize:'0.6rem', color:'#444', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:3 }}>Background</div>
                    <div style={{ fontSize:'0.82rem', color:'#888', fontFamily:'monospace' }}>{aiFactoryResult.config?.darkBg}</div>
                  </div>
                </div>

                <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:10, padding:'14px 16px', display:'flex', flexDirection:'column', gap:8 }}>
                  {[['Identifiant', aiFactoryResult.identifier], ['Dossier', `client/src/sites/${aiFactoryResult.allcaps}/`], ['Firestore', `websites/${aiFactoryResult.identifier}`]].map(([label, val]) => (
                    <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:'0.72rem', color:'#444', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em' }}>{label}</span>
                      <span style={{ fontSize:'0.78rem', color:'#888', fontFamily:'monospace' }}>{val}</span>
                    </div>
                  ))}
                </div>

                <div style={{ background:'rgba(200,255,0,0.04)', border:'1px solid rgba(200,255,0,0.12)', borderRadius:10, padding:'14px 16px' }}>
                  <div style={{ fontSize:'0.68rem', color:'#C8FF00', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:10 }}>Prochaines étapes</div>
                  {[`Personnalise : client/src/sites/${aiFactoryResult.allcaps}/config.js`, `Build : npm run build:${aiFactoryResult.identifier}`, `Deploy : firebase deploy --only hosting:${aiFactoryResult.identifier}`].map((step, i) => (
                    <div key={i} style={{ display:'flex', gap:10, marginBottom: i < 2 ? 7 : 0 }}>
                      <span style={{ fontSize:'0.72rem', color:'#C8FF00', fontWeight:800, flexShrink:0 }}>{`0${i+1}`}</span>
                      <span style={{ fontSize:'0.78rem', color:'#888', fontFamily:'monospace' }}>{step}</span>
                    </div>
                  ))}
                </div>

                <div style={{ display:'flex', gap:10 }}>
                  <button onClick={() => { setAiFactory(null); setAiFactoryResult(null); setSec(2); }}
                    style={{ flex:1, padding:'11px', background:'#C8FF00', color:'#000', border:'none', borderRadius:9, fontWeight:800, cursor:'pointer', fontSize:'0.86rem' }}>
                    Voir dans Sites web
                  </button>
                  <button onClick={() => { setAiFactoryResult(null); setAiFactory({ name:'', identifier:'', siteType:'vitrine', location:'Montréal, QC', description:'', customPhotoUrl:'' }); setAiFactoryError(''); }}
                    style={{ padding:'11px 18px', background:'rgba(255,255,255,0.05)', color:'#888', border:'1px solid rgba(255,255,255,0.08)', borderRadius:9, fontWeight:600, cursor:'pointer', fontSize:'0.86rem' }}>
                    Nouveau
                  </button>
                </div>
              </div>
            )}

            {!aiFactoryLoading && !aiFactoryResult && (
              <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={{ fontSize:'0.68rem', color:'#555', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', display:'block', marginBottom:6 }}>Nom du site *</label>
                    <input value={aiFactory.name} onChange={e => setAiFactory(f => ({ ...f, name: e.target.value, identifier: slugify(e.target.value) }))}
                      placeholder="ex: Boulangerie Lebeau"
                      style={{ width:'100%', background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:9, padding:'10px 13px', color:'#EAEAEA', fontSize:'0.9rem', outline:'none', boxSizing:'border-box' }}/>
                  </div>
                  <div>
                    <label style={{ fontSize:'0.68rem', color:'#555', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', display:'block', marginBottom:6 }}>Identifiant</label>
                    <input value={aiFactory.identifier} onChange={e => setAiFactory(f => ({ ...f, identifier: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'') }))}
                      placeholder="boulangerielebeau"
                      style={{ width:'100%', background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:9, padding:'10px 13px', color:'#888', fontSize:'0.82rem', outline:'none', fontFamily:'monospace', boxSizing:'border-box' }}/>
                  </div>
                  <div>
                    <label style={{ fontSize:'0.68rem', color:'#555', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', display:'block', marginBottom:6 }}>Secteur</label>
                    <select value={aiFactory.siteType} onChange={e => setAiFactory(f => ({ ...f, siteType: e.target.value }))}
                      style={{ width:'100%', background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:9, padding:'10px 13px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none', boxSizing:'border-box' }}>
                      {[['vitrine','Site vitrine'],['restaurant','Restaurant'],['coiffure','Coiffure'],['tech','Tech / SaaS'],['commerce','Commerce'],['sante','Santé'],['immobilier','Immobilier'],['fitness','Fitness'],['avocat','Avocat'],['construction','Construction'],['photo','Photographie'],['mode','Mode']].map(([v,l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={{ fontSize:'0.68rem', color:'#555', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', display:'block', marginBottom:6 }}>Ville</label>
                    <input value={aiFactory.location} onChange={e => setAiFactory(f => ({ ...f, location: e.target.value }))}
                      placeholder="Montréal, QC"
                      style={{ width:'100%', background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:9, padding:'10px 13px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none', boxSizing:'border-box' }}/>
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={{ fontSize:'0.68rem', color:'#555', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', display:'block', marginBottom:6 }}>Description du business *</label>
                    <textarea value={aiFactory.description} onChange={e => setAiFactory(f => ({ ...f, description: e.target.value }))}
                      placeholder="ex: Boulangerie artisanale fondée en 2010, spécialisée dans les pains au levain et viennoiseries. Clientèle locale, ambiance chaleureuse."
                      rows={3}
                      style={{ width:'100%', background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:9, padding:'10px 13px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none', resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }}/>
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={{ fontSize:'0.68rem', color:'#555', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', display:'block', marginBottom:6 }}>Photo hero <span style={{ color:'#333', fontWeight:500 }}>(URL, optionnel)</span></label>
                    <input value={aiFactory.customPhotoUrl} onChange={e => setAiFactory(f => ({ ...f, customPhotoUrl: e.target.value }))}
                      placeholder="https://..."
                      style={{ width:'100%', background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:9, padding:'10px 13px', color:'#888', fontSize:'0.82rem', outline:'none', fontFamily:'monospace', boxSizing:'border-box' }}/>
                  </div>
                </div>

                {aiFactoryError && (
                  <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:9, padding:'10px 14px', fontSize:'0.82rem', color:'#f87171' }}>
                    {aiFactoryError}
                  </div>
                )}

                <div style={{ display:'flex', gap:10, paddingTop:4 }}>
                  <button onClick={createSiteWithAI} disabled={!aiFactory.name || !aiFactory.description}
                    style={{ flex:1, padding:'12px', background: (!aiFactory.name || !aiFactory.description) ? 'rgba(200,255,0,0.3)' : '#C8FF00', color:'#000', border:'none', borderRadius:9, fontWeight:800, cursor: (!aiFactory.name || !aiFactory.description) ? 'not-allowed' : 'pointer', fontSize:'0.9rem', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                    {I.spark} Générer le site
                  </button>
                  <button onClick={() => setAiFactory(null)}
                    style={{ padding:'12px 18px', background:'rgba(255,255,255,0.05)', color:'#666', border:'1px solid rgba(255,255,255,0.08)', borderRadius:9, fontWeight:600, cursor:'pointer', fontSize:'0.86rem' }}>
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CONTRATS IA COMPONENT ───────────────────────────────────────────────────
const PROJECT_TYPES = ['Site vitrine','Site e-commerce','Landing page','Refonte de site','Application web','Site multipage','Maintenance & support'];

function ContractsSection({ contracts, ctModal, setCtModal, ctForm, setCtForm, ctGenerated, setCtGenerated, ctGenerating, setCtGenerating, ctSaving, setCtSaving, ctDelTarget, setCtDelTarget, ctViewing, setCtViewing, ax, firestore }) {

  const fld = (label, key, type='text', opts={}) => (
    <div style={{ marginBottom:12 }}>
      <label style={{ display:'block', fontSize:'0.7rem', color:'#444', marginBottom:5, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>{label}</label>
      <input type={type} value={ctForm[key]||''} onChange={e=>setCtForm(f=>({...f,[key]:e.target.value}))} {...opts}
        style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'9px 12px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none', boxSizing:'border-box' }}
        onFocus={e=>e.target.style.borderColor='rgba(200,255,0,0.35)'}
        onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.08)'} />
    </div>
  );

  const sel = (label, key, options) => (
    <div style={{ marginBottom:12 }}>
      <label style={{ display:'block', fontSize:'0.7rem', color:'#444', marginBottom:5, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>{label}</label>
      <select value={ctForm[key]||''} onChange={e=>setCtForm(f=>({...f,[key]:e.target.value}))}
        style={{ width:'100%', background:'#0e0e12', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'9px 12px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none', appearance:'none' }}>
        {options.map(o=><option key={o} style={{background:'#111'}}>{o}</option>)}
      </select>
    </div>
  );

  async function generate() {
    if (!ctForm.clientName || !ctForm.budget) { alert('Nom du client et budget requis.'); return; }
    setCtGenerating(true); setCtGenerated('');
    try {
      const { data } = await ax.post('/api/ai/generate-contract', ctForm);
      setCtGenerated(data.contract);
    } catch(e) { alert('Erreur IA : ' + (e.response?.data?.error || e.message)); }
    setCtGenerating(false);
  }

  async function saveContract() {
    if (!ctGenerated) return;
    setCtSaving(true);
    try {
      await addDoc(collection(firestore, 'contracts'), {
        clientName: ctForm.clientName,
        clientCompany: ctForm.clientCompany || '',
        projectType: ctForm.projectType,
        budget: ctForm.budget,
        currency: ctForm.currency || 'CAD',
        form: ctForm,
        text: ctGenerated,
        createdAt: serverTimestamp(),
      });
      setCtModal(false);
      setCtGenerated('');
      setCtForm(f=>({...f, clientName:'',clientCompany:'',clientEmail:'',clientPhone:'',clientAddress:'',projectDescription:'',budget:'',startDate:'',endDate:'',paymentTerms:'',extras:''}));
    } catch(e) { alert('Erreur sauvegarde : ' + e.message); }
    setCtSaving(false);
  }

  async function deleteContract(id) {
    await deleteDoc(doc(firestore, 'contracts', id));
    setCtDelTarget(null);
    if (ctViewing?.id === id) setCtViewing(null);
  }

  function printContract(ct) {
    const win = window.open('', '_blank');
    if (!win) { alert('Autorisez les popups.'); return; }
    win.document.write(`<!DOCTYPE html><html><head><title>Contrat — ${ct.clientName}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Georgia,serif;padding:40px 60px;color:#111;line-height:1.7;font-size:13px}h1{font-size:20px;margin-bottom:8px}pre{white-space:pre-wrap;font-family:Georgia,serif;font-size:13px;line-height:1.8}@media print{body{padding:20mm 25mm}@page{margin:20mm 25mm}}</style>
</head><body><pre>${ct.text}</pre></body></html>`);
    win.document.close();
    setTimeout(()=>win.print(),400);
  }

  const layout = { padding:'28px 32px', maxWidth:1100, margin:'0 auto' };
  const card2 = { background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:12, padding:20 };

  return (
    <div style={layout}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <h2 style={{ fontSize:'1.4rem', fontWeight:800, color:'#EAEAEA', letterSpacing:'-0.03em', marginBottom:4 }}>Contrats IA</h2>
          <p style={{ color:'#444', fontSize:'0.84rem' }}>Générez des contrats web professionnels en quelques secondes</p>
        </div>
        <button style={btn.primary} onClick={()=>{ setCtModal(true); setCtGenerated(''); }}>
          {I.spark} Nouveau contrat
        </button>
      </div>

      {/* Liste des contrats */}
      {contracts.length === 0 ? (
        <div style={{ ...card2, textAlign:'center', padding:'48px 24px' }}>
          <div style={{ fontSize:'2rem', marginBottom:12, opacity:0.3 }}>📄</div>
          <div style={{ color:'#444', fontSize:'0.88rem' }}>Aucun contrat généré. Cliquez sur "Nouveau contrat" pour commencer.</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {contracts.map(ct => (
            <div key={ct.id} style={{ ...card2, display:'flex', alignItems:'center', gap:16, transition:'border-color 0.15s', cursor:'pointer', borderColor: ctViewing?.id===ct.id ? 'rgba(200,255,0,0.25)' : 'rgba(255,255,255,0.07)' }}
              onClick={()=>setCtViewing(ctViewing?.id===ct.id?null:ct)}>
              <div style={{ width:40, height:40, borderRadius:10, background:'rgba(200,255,0,0.07)', border:'1px solid rgba(200,255,0,0.15)', display:'flex', alignItems:'center', justifyContent:'center', color:'#C8FF00', flexShrink:0 }}>
                {I.contract}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ color:'#EAEAEA', fontWeight:700, fontSize:'0.9rem' }}>{ct.clientName}{ct.clientCompany ? ` — ${ct.clientCompany}` : ''}</div>
                <div style={{ color:'#444', fontSize:'0.76rem', marginTop:2 }}>
                  {ct.projectType} · {ct.currency || 'CAD'} {ct.budget}
                  {ct.createdAt?.toDate ? ` · ${ct.createdAt.toDate().toLocaleDateString('fr-CA')}` : ''}
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
                <button onClick={()=>printContract(ct)} style={{ ...btn.outline, gap:5 }}>{I.print} Imprimer</button>
                <IconBtn icon={I.bin} danger onClick={()=>setCtDelTarget(ct)} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contract viewer */}
      {ctViewing && (
        <div style={{ ...card2, marginTop:16, borderColor:'rgba(200,255,0,0.15)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ color:'#C8FF00', fontWeight:700, fontSize:'0.88rem' }}>{ctViewing.clientName} — {ctViewing.projectType}</div>
            <button onClick={()=>printContract(ctViewing)} style={btn.outline}>{I.print} Imprimer / PDF</button>
          </div>
          <pre style={{ whiteSpace:'pre-wrap', color:'#888', fontSize:'0.82rem', lineHeight:1.8, fontFamily:'Georgia,serif', maxHeight:500, overflowY:'auto' }}>{ctViewing.text}</pre>
        </div>
      )}

      {/* Modal — formulaire + génération */}
      {ctModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', backdropFilter:'blur(10px)', zIndex:600, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'24px 16px', overflowY:'auto' }}
          onClick={()=>{ if(!ctGenerating && !ctSaving){ setCtModal(false); setCtGenerated(''); } }}>
          <div style={{ background:'#0e0e12', border:'1px solid rgba(255,255,255,0.08)', borderRadius:18, padding:32, width:'100%', maxWidth:800, marginTop:8 }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <h3 style={{ fontSize:'1.1rem', fontWeight:800, color:'#EAEAEA' }}>Nouveau contrat IA</h3>
              <button onClick={()=>{ setCtModal(false); setCtGenerated(''); }} style={{ background:'none', border:'none', color:'#444', fontSize:'1.4rem', cursor:'pointer' }}>×</button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
              {/* Colonne gauche — infos */}
              <div>
                <div style={{ fontSize:'0.7rem', color:'#C8FF00', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:14 }}>Informations client</div>
                {fld('Nom du client *', 'clientName')}
                {fld('Entreprise', 'clientCompany')}
                {fld('Courriel', 'clientEmail', 'email')}
                {fld('Téléphone', 'clientPhone', 'tel')}
                {fld('Adresse', 'clientAddress')}

                <div style={{ fontSize:'0.7rem', color:'#C8FF00', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', margin:'18px 0 14px' }}>Agence</div>
                {fld('Nom agence', 'agencyName')}
                {fld('Adresse agence', 'agencyAddress')}
                {fld('Courriel agence', 'agencyEmail', 'email')}
              </div>

              {/* Colonne droite — projet */}
              <div>
                <div style={{ fontSize:'0.7rem', color:'#C8FF00', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:14 }}>Détails du projet</div>
                {sel('Type de projet', 'projectType', PROJECT_TYPES)}
                <div style={{ marginBottom:12 }}>
                  <label style={{ display:'block', fontSize:'0.7rem', color:'#444', marginBottom:5, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>Description du projet</label>
                  <textarea value={ctForm.projectDescription} onChange={e=>setCtForm(f=>({...f,projectDescription:e.target.value}))} rows={3}
                    placeholder="Décrivez les fonctionnalités, pages, besoins spéciaux..."
                    style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'9px 12px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }}
                    onFocus={e=>e.target.style.borderColor='rgba(200,255,0,0.35)'}
                    onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.08)'} />
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {fld('Budget total *', 'budget', 'number')}
                  {sel('Devise', 'currency', ['CAD','USD','EUR'])}
                  {fld('Acompte (%)', 'deposit', 'number')}
                  {fld('Révisions incluses', 'revisions', 'number')}
                  {fld('Date de début', 'startDate', 'date')}
                  {fld('Date de livraison', 'endDate', 'date')}
                </div>
                {sel('Hébergement inclus', 'hosting', ['Non','Oui — 1 an','Oui — 2 ans'])}
                {sel('Maintenance post-livraison', 'maintenance', ['Non','1 mois inclus','3 mois inclus','6 mois inclus'])}
                <div style={{ marginBottom:12 }}>
                  <label style={{ display:'block', fontSize:'0.7rem', color:'#444', marginBottom:5, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>Clauses spéciales / extras</label>
                  <textarea value={ctForm.extras} onChange={e=>setCtForm(f=>({...f,extras:e.target.value}))} rows={2}
                    placeholder="Clauses particulières, mentions spéciales..."
                    style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'9px 12px', color:'#EAEAEA', fontSize:'0.86rem', outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }}
                    onFocus={e=>e.target.style.borderColor='rgba(200,255,0,0.35)'}
                    onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.08)'} />
                </div>
              </div>
            </div>

            {/* Bouton génération */}
            <div style={{ marginTop:20, display:'flex', gap:12, justifyContent:'flex-end' }}>
              <button onClick={()=>{ setCtModal(false); setCtGenerated(''); }} style={btn.ghost}>Annuler</button>
              <button onClick={generate} disabled={ctGenerating}
                style={{ ...btn.primary, opacity:ctGenerating?0.7:1, cursor:ctGenerating?'not-allowed':'pointer', minWidth:200 }}>
                {ctGenerating
                  ? <><span style={{ display:'inline-block', width:12, height:12, border:'2px solid #000', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/> Génération en cours...</>
                  : <>{I.spark} Générer le contrat IA</>}
              </button>
            </div>

            {/* Résultat */}
            {ctGenerated && (
              <div style={{ marginTop:24 }}>
                <div style={{ fontSize:'0.7rem', color:'#C8FF00', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:10 }}>Contrat généré</div>
                <textarea value={ctGenerated} onChange={e=>setCtGenerated(e.target.value)} rows={18}
                  style={{ width:'100%', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(200,255,0,0.2)', borderRadius:10, padding:'14px 16px', color:'#CCC', fontSize:'0.82rem', lineHeight:1.8, outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'Georgia,serif' }} />
                <div style={{ display:'flex', gap:10, marginTop:14, justifyContent:'flex-end' }}>
                  <button onClick={generate} disabled={ctGenerating} style={btn.ghost}>{I.spark} Régénérer</button>
                  <button onClick={()=>printContract({ clientName:ctForm.clientName, projectType:ctForm.projectType, text:ctGenerated })} style={btn.outline}>{I.print} Aperçu / PDF</button>
                  <button onClick={saveContract} disabled={ctSaving}
                    style={{ ...btn.primary, opacity:ctSaving?0.7:1 }}>
                    {ctSaving ? 'Sauvegarde...' : '✓ Sauvegarder'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {ctDelTarget && (
        <Confirm name={ctDelTarget.clientName} onConfirm={()=>deleteContract(ctDelTarget.id)} onClose={()=>setCtDelTarget(null)} />
      )}
    </div>
  );
}

// ─── STYLE HELPERS ────────────────────────────────────────────────────────────
const filterPill = (active) => ({
  display:'inline-flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:99,
  border:`1px solid ${active?'var(--accent-border,rgba(200,255,0,0.3))':'rgba(255,255,255,0.07)'}`,
  background: active?'var(--accent-bg,rgba(200,255,0,0.08))':'transparent',
  color: active?'var(--accent,#C8FF00)':'#444',
  fontSize:'0.76rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.04em',
  transition:'all 0.12s',
});

const pillCount = {
  background:'rgba(255,255,255,0.08)', color:'#555', borderRadius:99,
  fontSize:'0.65rem', fontWeight:800, padding:'1px 6px', marginLeft:2,
};

const td = {
  padding:'13px 16px', fontSize:'0.84rem', color:'#888', verticalAlign:'middle',
};

/* ══════════════════════════════════════════════════════
   AUDITS SEO SECTION
══════════════════════════════════════════════════════ */
function AuditsSection({ audits, competitorReports = [], gmbReports = [] }) {
  const [selected,     setSelected]     = React.useState(null);
  const [selectedComp, setSelectedComp] = React.useState(null);
  const [selectedGmb,  setSelectedGmb]  = React.useState(null);
  const [tab,          setTab]          = React.useState('audits');

  const auditRevenu = audits.reduce((s, a) => s + (a.amount || 0), 0);
  const compRevenu  = competitorReports.reduce((s, r) => s + (r.amount || 0), 0);
  const gmbRevenu   = gmbReports.reduce((s, r) => s + (r.amount || 0), 0);
  const totalRevenu = auditRevenu + compRevenu + gmbRevenu;
  const proCount    = audits.filter(a => a.plan === 'pro').length;
  const essCount    = audits.filter(a => a.plan === 'essential').length;
  const compCount   = competitorReports.length;
  const gmbCount    = gmbReports.length;

  const scoreColor = s => s >= 70 ? '#4ade80' : s >= 40 ? '#f59e0b' : '#ef4444';

  const planBadge = plan => plan === 'pro'
    ? <span style={{ display:'inline-block', background:'rgba(255,77,28,0.15)', border:'1px solid rgba(255,77,28,0.35)', color:'#FF4D1C', fontSize:'0.65rem', fontWeight:800, padding:'2px 8px', borderRadius:99, letterSpacing:1, textTransform:'uppercase' }}>Pro · 499$</span>
    : <span style={{ display:'inline-block', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', color:'#666', fontSize:'0.65rem', fontWeight:700, padding:'2px 8px', borderRadius:99, letterSpacing:1, textTransform:'uppercase' }}>Essentiel · 299$</span>;

  const fmt = iso => iso ? new Date(iso).toLocaleDateString('fr-CA', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  const tabStyle = active => ({
    padding:'7px 18px', borderRadius:8, fontSize:'0.78rem', fontWeight:700, cursor:'pointer', border:'none',
    background: active ? 'var(--accent-bg,rgba(200,255,0,0.1))' : 'transparent',
    color: active ? 'var(--accent,#C8FF00)' : '#444',
    borderBottom: active ? '2px solid var(--accent,#C8FF00)' : '2px solid transparent',
    transition:'all 0.15s',
  });

  return (
    <div style={{ padding:'28px 32px', animation:'fadeIn 0.25s ease' }}>

      {/* En-tête */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ fontSize:'1.25rem', fontWeight:800, color:'#e2e8f0', marginBottom:4 }}>Ventes Axispme</h2>
          <p style={{ fontSize:'0.8rem', color:'#444' }}>{audits.length + competitorReports.length + gmbReports.length} commande{(audits.length + competitorReports.length + gmbReports.length) !== 1 ? 's' : ''} · via axispme.ca</p>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <div style={{ background:'#111', border:'1px solid #1e1e1e', borderRadius:12, padding:'12px 20px', textAlign:'center' }}>
            <div style={{ fontSize:'1.4rem', fontWeight:900, color:'var(--accent,#C8FF00)' }}>{totalRevenu}$</div>
            <div style={{ fontSize:'0.68rem', color:'#444', marginTop:2, textTransform:'uppercase', letterSpacing:1 }}>Revenu total</div>
          </div>
          <div style={{ background:'#111', border:'1px solid #1e1e1e', borderRadius:12, padding:'12px 20px', textAlign:'center' }}>
            <div style={{ fontSize:'1.4rem', fontWeight:900, color:'#FF4D1C' }}>{proCount}</div>
            <div style={{ fontSize:'0.68rem', color:'#444', marginTop:2, textTransform:'uppercase', letterSpacing:1 }}>Pro · 499$</div>
          </div>
          <div style={{ background:'#111', border:'1px solid #1e1e1e', borderRadius:12, padding:'12px 20px', textAlign:'center' }}>
            <div style={{ fontSize:'1.4rem', fontWeight:900, color:'#e2e8f0' }}>{essCount}</div>
            <div style={{ fontSize:'0.68rem', color:'#444', marginTop:2, textTransform:'uppercase', letterSpacing:1 }}>Essentiel · 299$</div>
          </div>
          <div style={{ background:'#111', border:'1px solid rgba(139,92,246,0.3)', borderRadius:12, padding:'12px 20px', textAlign:'center' }}>
            <div style={{ fontSize:'1.4rem', fontWeight:900, color:'#a78bfa' }}>{compCount}</div>
            <div style={{ fontSize:'0.68rem', color:'#444', marginTop:2, textTransform:'uppercase', letterSpacing:1 }}>Concurrent · 599$</div>
          </div>
          <div style={{ background:'#111', border:'1px solid rgba(52,211,153,0.3)', borderRadius:12, padding:'12px 20px', textAlign:'center' }}>
            <div style={{ fontSize:'1.4rem', fontWeight:900, color:'#34d399' }}>{gmbCount}</div>
            <div style={{ fontSize:'0.68rem', color:'#444', marginTop:2, textTransform:'uppercase', letterSpacing:1 }}>GMB · 199$</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'1px solid #1a1a1a', paddingBottom:0 }}>
        <button style={tabStyle(tab === 'audits')} onClick={() => setTab('audits')}>
          Audits SEO ({audits.length})
        </button>
        <button style={tabStyle(tab === 'competitor')} onClick={() => setTab('competitor')}>
          Analyses concurrentielles ({compCount})
        </button>
        <button style={tabStyle(tab === 'gmb')} onClick={() => setTab('gmb')}>
          Audits GMB ({gmbCount})
        </button>
      </div>

      {/* TABLE AUDITS */}
      {tab === 'audits' && (audits.length === 0 ? (
        <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:16, padding:'60px 32px', textAlign:'center' }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📊</div>
          <div style={{ fontSize:'1rem', fontWeight:700, color:'#333', marginBottom:6 }}>Aucun audit vendu pour l'instant</div>
          <div style={{ fontSize:'0.8rem', color:'#2a2a2a' }}>Les commandes de axispme.ca apparaîtront ici automatiquement.</div>
        </div>
      ) : (
        <div style={{ background:'#0d0d0f', border:'1px solid #1a1a1a', borderRadius:16, overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr 1fr 0.6fr 1fr 1.2fr', gap:0, padding:'10px 16px', borderBottom:'1px solid #1a1a1a', background:'#111' }}>
            {['Client','URL analysée','Forfait','Score','Langue','Date','Action'].map(h => (
              <div key={h} style={{ fontSize:'0.65rem', fontWeight:800, color:'#333', textTransform:'uppercase', letterSpacing:1 }}>{h}</div>
            ))}
          </div>
          {audits.map(a => (
            <div key={a.id} style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr 1fr 0.6fr 1fr 1.2fr', gap:0, padding:'14px 16px', borderBottom:'1px solid #111', alignItems:'center', transition:'background 0.15s', cursor:'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background='#121214'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
              onClick={() => setSelected(a)}
            >
              <div style={{ fontSize:'0.83rem', color:'#c0c0c0', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.email}</div>
              <div style={{ fontSize:'0.78rem', color:'#555', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.url?.replace(/^https?:\/\//, '')}</div>
              <div>{planBadge(a.plan)}</div>
              <div>
                <span style={{ fontSize:'0.9rem', fontWeight:900, color: scoreColor(a.score) }}>{a.score}</span>
                <span style={{ fontSize:'0.65rem', color:'#333' }}>/100</span>
              </div>
              <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#555', textTransform:'uppercase', letterSpacing:1 }}>{a.lang || 'fr'}</div>
              <div style={{ fontSize:'0.72rem', color:'#444' }}>{fmt(a.createdAt)}</div>
              <div>
                <button onClick={e => { e.stopPropagation(); setSelected(a); }} style={{ background:'rgba(200,255,0,0.06)', border:'1px solid rgba(200,255,0,0.15)', color:'#C8FF00', borderRadius:8, padding:'5px 12px', fontSize:'0.7rem', fontWeight:700, cursor:'pointer' }}>
                  Détails →
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* TABLE CONCURRENT */}
      {tab === 'competitor' && (competitorReports.length === 0 ? (
        <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:16, padding:'60px 32px', textAlign:'center' }}>
          <div style={{ fontSize:36, marginBottom:12 }}>⚡</div>
          <div style={{ fontSize:'1rem', fontWeight:700, color:'#333', marginBottom:6 }}>Aucune analyse concurrentielle pour l'instant</div>
          <div style={{ fontSize:'0.8rem', color:'#2a2a2a' }}>Les commandes apparaîtront ici automatiquement.</div>
        </div>
      ) : (
        <div style={{ background:'#0d0d0f', border:'1px solid #1a1a1a', borderRadius:16, overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1.5fr 1fr 0.6fr 1fr 1.2fr', gap:0, padding:'10px 16px', borderBottom:'1px solid #1a1a1a', background:'#111' }}>
            {['Client','Votre site','Concurrents','Score vous','Langue','Date','Action'].map(h => (
              <div key={h} style={{ fontSize:'0.65rem', fontWeight:800, color:'#333', textTransform:'uppercase', letterSpacing:1 }}>{h}</div>
            ))}
          </div>
          {competitorReports.map(r => (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1.5fr 1fr 0.6fr 1fr 1.2fr', gap:0, padding:'14px 16px', borderBottom:'1px solid #111', alignItems:'center', transition:'background 0.15s', cursor:'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background='#121214'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
              onClick={() => setSelectedComp(r)}
            >
              <div style={{ fontSize:'0.83rem', color:'#c0c0c0', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.email}</div>
              <div style={{ fontSize:'0.78rem', color:'#555', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.yourUrl?.replace(/^https?:\/\//, '')}</div>
              <div style={{ fontSize:'0.72rem', color:'#555' }}>{(r.competitorUrls || []).length} concurrent{(r.competitorUrls || []).length > 1 ? 's' : ''}</div>
              <div>
                <span style={{ fontSize:'0.9rem', fontWeight:900, color: scoreColor(r.yourScore) }}>{r.yourScore ?? '—'}</span>
                <span style={{ fontSize:'0.65rem', color:'#333' }}>/100</span>
              </div>
              <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#555', textTransform:'uppercase', letterSpacing:1 }}>{r.lang || 'fr'}</div>
              <div style={{ fontSize:'0.72rem', color:'#444' }}>{fmt(r.createdAt)}</div>
              <div>
                <button onClick={e => { e.stopPropagation(); setSelectedComp(r); }} style={{ background:'rgba(167,139,250,0.06)', border:'1px solid rgba(167,139,250,0.2)', color:'#a78bfa', borderRadius:8, padding:'5px 12px', fontSize:'0.7rem', fontWeight:700, cursor:'pointer' }}>
                  Détails →
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* TABLE GMB */}
      {tab === 'gmb' && (gmbReports.length === 0 ? (
        <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:16, padding:'60px 32px', textAlign:'center' }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📍</div>
          <div style={{ fontSize:'1rem', fontWeight:700, color:'#333', marginBottom:6 }}>Aucun audit GMB pour l'instant</div>
          <div style={{ fontSize:'0.8rem', color:'#2a2a2a' }}>Les commandes apparaîtront ici automatiquement.</div>
        </div>
      ) : (
        <div style={{ background:'#0d0d0f', border:'1px solid #1a1a1a', borderRadius:16, overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr 1fr 0.6fr 1fr 1.2fr', gap:0, padding:'10px 16px', borderBottom:'1px solid #1a1a1a', background:'#111' }}>
            {['Client','Entreprise','Score','Avis','Langue','Date','Action'].map(h => (
              <div key={h} style={{ fontSize:'0.65rem', fontWeight:800, color:'#333', textTransform:'uppercase', letterSpacing:1 }}>{h}</div>
            ))}
          </div>
          {gmbReports.map(r => (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr 1fr 0.6fr 1fr 1.2fr', gap:0, padding:'14px 16px', borderBottom:'1px solid #111', alignItems:'center', transition:'background 0.15s', cursor:'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background='#121214'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
              onClick={() => setSelectedGmb(r)}
            >
              <div style={{ fontSize:'0.83rem', color:'#c0c0c0', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.email}</div>
              <div style={{ fontSize:'0.78rem', color:'#555', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.businessName} · {r.city}</div>
              <div>
                <span style={{ fontSize:'0.9rem', fontWeight:900, color: scoreColor(r.score) }}>{r.score}</span>
                <span style={{ fontSize:'0.65rem', color:'#333' }}>/100</span>
              </div>
              <div style={{ fontSize:'0.78rem', color:'#555' }}>{r.reviews ?? '—'} {r.rating ? `· ${r.rating}⭐` : ''}</div>
              <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#555', textTransform:'uppercase', letterSpacing:1 }}>{r.lang || 'fr'}</div>
              <div style={{ fontSize:'0.72rem', color:'#444' }}>{fmt(r.createdAt)}</div>
              <div>
                <button onClick={e => { e.stopPropagation(); setSelectedGmb(r); }} style={{ background:'rgba(52,211,153,0.06)', border:'1px solid rgba(52,211,153,0.2)', color:'#34d399', borderRadius:8, padding:'5px 12px', fontSize:'0.7rem', fontWeight:700, cursor:'pointer' }}>
                  Détails →
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Modal détail */}
      {selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}
          onClick={() => setSelected(null)}>
          <div style={{ background:'#0e0e12', border:'1px solid #1e1e1e', borderRadius:20, padding:32, maxWidth:520, width:'100%', maxHeight:'85vh', overflowY:'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
              <div>
                <div style={{ fontSize:'0.7rem', color:'#444', textTransform:'uppercase', letterSpacing:2, marginBottom:6 }}>Détail de la commande</div>
                <div style={{ fontSize:'1.1rem', fontWeight:800, color:'#e2e8f0' }}>{selected.email}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#555', padding:'6px 10px', cursor:'pointer', fontSize:'0.8rem' }}>✕</button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
              {[
                { label:'Forfait',  value: selected.plan === 'pro' ? '✦ Pro — 499$' : 'Essentiel — 299$', color: selected.plan === 'pro' ? '#FF4D1C' : '#666' },
                { label:'Score SEO', value: `${selected.score}/100`, color: scoreColor(selected.score) },
                { label:'Montant',  value: `${selected.amount}$ CAD`, color:'#C8FF00' },
                { label:'Langue',   value: (selected.lang || 'fr').toUpperCase(), color:'#888' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px' }}>
                  <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:'0.95rem', fontWeight:800, color }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
              <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>URL analysée</div>
              <a href={selected.url} target="_blank" rel="noreferrer" style={{ fontSize:'0.85rem', color:'#C8FF00', wordBreak:'break-all' }}>{selected.url}</a>
            </div>

            <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
              <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Date de commande</div>
              <div style={{ fontSize:'0.85rem', color:'#888' }}>{fmt(selected.createdAt)}</div>
            </div>

            {selected.stripeSessionId && (
              <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px' }}>
                <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Stripe Session ID</div>
                <div style={{ fontSize:'0.72rem', color:'#333', wordBreak:'break-all', fontFamily:'monospace' }}>{selected.stripeSessionId}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal détail — Concurrent */}
      {selectedComp && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}
          onClick={() => setSelectedComp(null)}>
          <div style={{ background:'#0e0e12', border:'1px solid rgba(139,92,246,0.3)', borderRadius:20, padding:32, maxWidth:560, width:'100%', maxHeight:'85vh', overflowY:'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
              <div>
                <div style={{ fontSize:'0.7rem', color:'#a78bfa', textTransform:'uppercase', letterSpacing:2, marginBottom:6 }}>Analyse Concurrentielle · 599$</div>
                <div style={{ fontSize:'1.1rem', fontWeight:800, color:'#e2e8f0' }}>{selectedComp.email}</div>
              </div>
              <button onClick={() => setSelectedComp(null)} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#555', padding:'6px 10px', cursor:'pointer', fontSize:'0.8rem' }}>✕</button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
              {[
                { label:'Score votre site', value: `${selectedComp.yourScore ?? '—'}/100`, color: scoreColor(selectedComp.yourScore) },
                { label:'Montant', value: `${selectedComp.amount || 599}$ CAD`, color:'#C8FF00' },
                { label:'Concurrents analysés', value: (selectedComp.competitorUrls || []).length, color:'#a78bfa' },
                { label:'Langue', value: (selectedComp.lang || 'fr').toUpperCase(), color:'#888' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px' }}>
                  <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:'0.95rem', fontWeight:800, color }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
              <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Votre site</div>
              <a href={selectedComp.yourUrl} target="_blank" rel="noreferrer" style={{ fontSize:'0.85rem', color:'#C8FF00', wordBreak:'break-all' }}>{selectedComp.yourUrl}</a>
            </div>

            {(selectedComp.competitorUrls || []).length > 0 && (
              <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
                <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Concurrents analysés</div>
                {(selectedComp.competitorUrls || []).map((u, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                    <span style={{ fontSize:'0.65rem', color:'#a78bfa', fontWeight:700 }}>{i + 1}.</span>
                    <a href={u} target="_blank" rel="noreferrer" style={{ fontSize:'0.82rem', color:'#888', wordBreak:'break-all' }}>{u}</a>
                  </div>
                ))}
              </div>
            )}

            {(selectedComp.competitorScores || []).length > 0 && (
              <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
                <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Scores comparatifs</div>
                {selectedComp.competitorScores.map((c, i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                    <span style={{ fontSize:'0.78rem', color:'#555', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'70%' }}>{c.url?.replace(/^https?:\/\//, '')}</span>
                    <span style={{ fontSize:'0.88rem', fontWeight:800, color: scoreColor(c.score) }}>{c.score}/100</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
              <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Date de commande</div>
              <div style={{ fontSize:'0.85rem', color:'#888' }}>{fmt(selectedComp.createdAt)}</div>
            </div>

            {selectedComp.stripeSessionId && (
              <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px' }}>
                <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Stripe Session ID</div>
                <div style={{ fontSize:'0.72rem', color:'#333', wordBreak:'break-all', fontFamily:'monospace' }}>{selectedComp.stripeSessionId}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal détail — GMB */}
      {selectedGmb && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}
          onClick={() => setSelectedGmb(null)}>
          <div style={{ background:'#0e0e12', border:'1px solid rgba(52,211,153,0.3)', borderRadius:20, padding:32, maxWidth:520, width:'100%', maxHeight:'85vh', overflowY:'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
              <div>
                <div style={{ fontSize:'0.7rem', color:'#34d399', textTransform:'uppercase', letterSpacing:2, marginBottom:6 }}>Audit Google My Business · 199$</div>
                <div style={{ fontSize:'1.1rem', fontWeight:800, color:'#e2e8f0' }}>{selectedGmb.email}</div>
              </div>
              <button onClick={() => setSelectedGmb(null)} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#555', padding:'6px 10px', cursor:'pointer', fontSize:'0.8rem' }}>✕</button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
              {[
                { label:'Score GMB',  value:`${selectedGmb.score}/100`,       color: scoreColor(selectedGmb.score) },
                { label:'Montant',    value:`${selectedGmb.amount||199}$ CAD`, color:'#C8FF00' },
                { label:'Note',       value: selectedGmb.rating ? `${selectedGmb.rating}/5 ⭐` : '—', color:'#f59e0b' },
                { label:'Avis',       value: selectedGmb.reviews ?? '—',      color:'#888' },
                { label:'Photos',     value: selectedGmb.photos ?? '—',       color:'#888' },
                { label:'Langue',     value:(selectedGmb.lang||'fr').toUpperCase(), color:'#888' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px' }}>
                  <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:'0.95rem', fontWeight:800, color }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
              <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Entreprise auditée</div>
              <div style={{ fontSize:'0.88rem', color:'#e2e8f0', fontWeight:700 }}>{selectedGmb.businessName}</div>
              <div style={{ fontSize:'0.78rem', color:'#555', marginTop:2 }}>{selectedGmb.city}</div>
            </div>

            <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
              <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Présence GMB</div>
              {[
                ['Site web',   selectedGmb.hasWebsite],
                ['Téléphone',  selectedGmb.hasPhone],
                ["Horaires",   selectedGmb.hasHours],
              ].map(([label, val]) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ fontSize:'0.8rem', color:'#555' }}>{label}</span>
                  <span style={{ fontSize:'0.8rem', fontWeight:700, color: val ? '#4ade80' : '#ef4444' }}>{val ? '✓ Oui' : '✗ Non'}</span>
                </div>
              ))}
            </div>

            <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
              <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Date de commande</div>
              <div style={{ fontSize:'0.85rem', color:'#888' }}>{fmt(selectedGmb.createdAt)}</div>
            </div>

            {selectedGmb.stripeSessionId && (
              <div style={{ background:'#111', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px' }}>
                <div style={{ fontSize:'0.65rem', color:'#444', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Stripe Session ID</div>
                <div style={{ fontSize:'0.72rem', color:'#333', wordBreak:'break-all', fontFamily:'monospace' }}>{selectedGmb.stripeSessionId}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}